import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import useFilterPersistence from "../../../hooks/useFilterPersistence";

import { makeStyles } from "@material-ui/core/styles";
import { dataService } from "../../../Services/data/data.service";
import { parseDateToDDMMYYTime, parseNewDateToDDMMYYYWithoutSeconds } from "../../../Utils/dateHelper";
import {
  valueTypeCodes,
  controlTypes,
  ProjectFilterTypes,
  resultColumns,
  profitLossResultColumns,
  graphTypes,
  externalApiTypes,
  timeLineVisualisationTypes,
  registrationPointColors,
  numericFilterOperations,
} from "../../../Config/constants";

// Packages
import { Spin } from "react-cssfx-loading";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";

// Components
import CompactDateRangePicker from "../../Shared/Buttons/CompactDateRangePicker";
import TimeRangeSelector from "../../Shared/Buttons/TimeRangeSelector";
import TimeRangeScroll from "../../Shared/Buttons/TimeRangeScroll";
import DateRangeSlider from "../../Shared/DateSelection/DateRangeSlider";
import DevExDataTable from "../Results/Components/DevExDataTable";
import ProfitLossDevExDataTable from "./Components/ProfitLossDevExDataTable";
import BoolToggle from "../../Shared/Buttons/BoolToggle";
import YamazumiChart from "./Components/YamazumiChart";
import OutputChart from "./Components/OutputChart";
import TimeLineChart from "../Results/Components/TimeLineChart";
import FinancialChart from "./Components/FinancialChart";
import { Box, Tooltip, Divider } from "@mui/material";
import EventChart from "../ProjectReport/Components/EventChart";
import TableHighLow from "../ProjectReport/Components/TableHighLow";
import FinancialSummaryTable from "./Components/FinancialSummaryTable";
import ProfitLossReport from "./Components/ProfitLossReport";
import GraphTabs from "./Components/GraphTabs";
import InsightsTab from "./Components/InsightsTab";
import TextWithReadMore from "../../Shared/TextWithReadMore";
import ProjectOrderBar from "./Components/ProjectOrderBar";
import Button from "../../Shared/Buttons/Button";

// Assets
import { Share, MapPin, CalendarClock, ChevronLeft, Plus, Minus, Timer, ScanBarcode, Image, TextCursorInput, LinkIcon, List, DiamondPlus, Pencil } from "lucide-react";

function GraphDetailPage(props) {
  const { graphId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();

  const viewRangeKey = `app:view-range:graph-view-${graphId}`;

  const { extraParams, setExtraParam } = useFilterPersistence({
    pageKey: `graph-view-${graphId}`,
    filterSchema: {},
    extraParamDefaults: {
      tab: graphTypes.INSIGHTS,
      startDate: '',
      endDate: '',
      compare: false,
      showLinear: false,
      labelVisible: true,
      percentVis: false,
      showRecurring: false,
      timelineView: 'timeline',
      dataFilters: '[]',
      financialFilters: '[]',
      zoom: '1 Week',
      viewFrom: '',
      viewTo: '',
    },
  });

  // General
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false); // Are measurements being fetched?
  const [selectedGraphType, setSelectedGraphType] = useState(extraParams.tab); // Default graph type
  const [graphProjects, setGraphProjects] = useState([]);
  const [allFilters, setAllFilters] = useState();
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [orderedProjectNames, setOrderedProjectNames] = useState([]);
  const [measurementsToDisplay, setMeasurementsToDisplay] = useState([]);

  const [originalComparePeriodMeasurements, setOriginalComparePeriodMeasurements] = useState([]); // Original measurements for compare period - should not be overwritten once set
  const [comparePeriodMeasurements, setComparePeriodMeasurements] = useState([]); // Measurements yamazumi & output charts use to compare previous period

  // Datagrid properties
  const [originalMeasurementRows, setOriginalMeasurementRows] = useState([]); // All measurementrows for current GraphDetail - should not be overwritten
  const [types, setTypes] = useState([]);

  // Filtering
  const [timeRangeDates, setTimeRangeDates] = useState({
    fromDate: new Date(Date.now() - 86400000),
    startDate: new Date(Date.now() - 86400000),
    toDate: new Date(Date.now()),
    endDate: new Date(Date.now()),
  });
  const [noGraphFound, setNoGraphFound] = useState(false);
  const [graphConfigurationObject, setGraphConfigurationObject] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [graphConfigurationName, setGraphConfigurationName] = useState("");
  const [selectedProject, setSelectedProject] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [graphConfigurationId, setGraphConfigurationId] = useState(null);
  const [graphConfigurationUniqueId, setGraphConfigurationUniqueId] = useState(null);
  const [labelVisible, setLabelVisible] = useState(extraParams.labelVisible);
  const [comparePreviousPeriod, setComparePreviousPeriod] = useState(extraParams.compare);

  // Timeline chart
  const [chartRows, setChartRows] = useState([]);
  const [graphDetails, setGraphDetails] = useState([]);
  const [showLinearTimeline, setShowLinearTimeline] = useState(extraParams.showLinear);
  const [timelineChartView, setTimelineChartView] = useState(extraParams.timelineView);
  const [timeRangeZoom, setTimeRangeZoom] = useState(() => {
    if (extraParams.viewFrom) return extraParams.zoom || '1 Week';
    try { const s = JSON.parse(localStorage.getItem(viewRangeKey)); return s?.zoom || '1 Week'; } catch { return '1 Week'; }
  });
  const [viewRangeDates, setViewRangeDates] = useState(() => {
    if (extraParams.viewFrom && extraParams.viewTo) {
      return { fromDate: new Date(extraParams.viewFrom), endDate: new Date(extraParams.viewTo) };
    }
    try {
      const s = JSON.parse(localStorage.getItem(viewRangeKey));
      if (s?.viewFrom && s?.viewTo) return { fromDate: new Date(s.viewFrom), endDate: new Date(s.viewTo) };
    } catch {}
    const now = new Date();
    return { fromDate: new Date(now.getTime() - 7 * 86400000), endDate: now };
  });

  // Financial chart
  const summaryRef = React.createRef();
  const highLowRef = React.createRef();
  const [financialChartData, setFinancialChartData] = useState([]);
  const [projectDetailNames, setDetailsNames] = useState({});
  const [totalPrice, setTotalPrice] = useState(0); // for summary
  const [priceData, setPriceData] = useState([]);
  const [highLowGraphProjects, setHighLowGraphProjects] = useState([]);

  // Profit/Loss report
  const profitLossReportComponentRef = useRef(null);
  const [profitLossReportData, setProfitLossReportData] = useState([]);
  const [allAccountCategories, setAllAccountCategories] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [allPriceBundles, setAllPriceBundles] = useState([]);
  const [boolCompareDate, setBoolCompareDate] = useState(false);
  const [profitLossDates, setProfitLossDates] = useState(null);
  const [recurringFinancials, setRecurringFinancials] = useState([]);
  const [showRecurringPrices, setShowRecurringPrices] = useState(extraParams.showRecurring);
  const [compareLimit, setCompareLimit] = useState(5);
  const [percentageVisible, setPercentageVisible] = useState(extraParams.percentVis);

  // Date range filters
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  //DevExData
  const [selection, setSelection] = useState([]);
  const [profitLossSelection, setProfitLossSelection] = useState([]);

  // Tab-specific filtering
  const [dataFilters, setDataFilters] = useState(() => { try { return JSON.parse(extraParams.dataFilters); } catch { return []; } });
  const [financialFilters, setFinancialFilters] = useState(() => { try { return JSON.parse(extraParams.financialFilters); } catch { return []; } });

  const [dataMeasurementRows, setDataMeasurementRows] = useState([]);
  const [financialMeasurementRows, setFinancialMeasurementRows] = useState([]);

  const yamazumiMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          row.stopTime !== null &&
          (row.typeId === controlTypes.STOPWATCH ||
            (row.typeId === controlTypes.EXTERNALAPI &&
              row.externalApiTypeId === externalApiTypes.STOPWATCH))
      ),
    [dataMeasurementRows]
  );

  const outputMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          row.typeId === controlTypes.COUNTER ||
          row.typeId === controlTypes.DATALIST ||
          row.typeId === controlTypes.FREETEXT ||
          row.typeId === controlTypes.SCANNER ||
          (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.DATA) ||
          (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.NUMERIC)
      ),
    [dataMeasurementRows]
  );

  const timelineEventMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          !(row.typeId === controlTypes.STOPWATCH ||
            (row.typeId === controlTypes.EXTERNALAPI &&
              row.externalApiTypeId === externalApiTypes.STOPWATCH))
      ),
    [dataMeasurementRows]
  );

  const viewFilteredMeasurements = useMemo(() => {
    if (!viewRangeDates?.fromDate || !viewRangeDates?.endDate) return measurementsToDisplay;
    const from = viewRangeDates.fromDate.getTime();
    const to = viewRangeDates.endDate.getTime();
    return measurementsToDisplay.filter((m) => {
      let t = null;
      if (m.startTime) { const v = new Date(m.startTime).getTime(); if (!isNaN(v)) t = v; }
      if (t === null && m.insertedOn) { const v = new Date(m.insertedOn).getTime(); if (!isNaN(v)) t = v; }
      return t === null || (t >= from && t <= to);
    });
  }, [measurementsToDisplay, viewRangeDates]);

  // Projection (Financial projection)
  const [activeProjection, setActiveProjection] = useState(false);

  useEffect(() => {
    if (graphId !== null && graphId !== "") {
      getGraphData(graphId);
    }
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Price bundles power the per-row breakdown tooltip on all result tables
  // (not just the financial one), so prefetch them as soon as the graph id is
  // known instead of waiting for the user to open the financial tab.
  useEffect(() => {
    if (!graphId) return;
    dataService
      .getAllPriceBundlesForGraphConfigurationByUniqueIdPublic(graphId)
      .then((result) => setAllPriceBundles(result || []))
      .catch(() => {});
  }, [graphId]);

  // Persist tab and toggle state
  useEffect(() => { setExtraParam('tab', selectedGraphType); }, [selectedGraphType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('compare', comparePreviousPeriod); }, [comparePreviousPeriod]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('showLinear', showLinearTimeline); }, [showLinearTimeline]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('labelVisible', labelVisible); }, [labelVisible]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('percentVis', percentageVisible); }, [percentageVisible]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('showRecurring', showRecurringPrices); }, [showRecurringPrices]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('timelineView', timelineChartView); }, [timelineChartView]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('dataFilters', JSON.stringify(dataFilters)); }, [dataFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('financialFilters', JSON.stringify(financialFilters)); }, [financialFilters]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    // Get all currencies
    dataService.getAllCurrencies().then((results) => {
      setAllCurrencies(results);
    });
  }, []);

  useEffect(() => {
    // set active currency
    if (selectedProjects.length > 0 && allCurrencies.length > 0) {
      let currency = allCurrencies.find((x) => x.id === selectedProjects[0].currencyId);
      setActiveCurrency(currency);
    }
  }, [selectedProjects, allCurrencies]);

  useEffect(() => {
    dataService.getTypes().then((results) => {
      setTypes(results.sort((a, b) => a.description.localeCompare(b.description)));
    });
  }, [props]);

  // Re-format P&L report when recurring prices toggle or data changes
  useEffect(() => {
    if (!loading && profitLossDates) {
      let measurements = financialMeasurementRows.filter(x => x.checked);
      formatProfitLossReportData(measurements, profitLossDates).then((formattedData) => {
        setProfitLossReportData(formattedData);
      });
    }
  }, [showRecurringPrices, recurringFinancials]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compare for yamazumi and output charts
  // Uses the current scroll/zoom window (viewRangeDates) so the comparison
  // always reflects the period immediately preceding what is visible in the chart.
  useEffect(() => {
    if (comparePreviousPeriod && viewRangeDates?.fromDate && viewRangeDates?.endDate) {
      handleComparePeriod({ fromDate: viewRangeDates.fromDate, toDate: viewRangeDates.endDate });
    } else {
      // Reset comparePeriodMeasurements when not comparing
      setComparePeriodMeasurements([]);
    }
  }, [comparePreviousPeriod, viewRangeDates]);

  const roundToSecond = (date) => {
    // remove milliseconds
    return new Date(date.setMilliseconds(0));
  };

  // Convert seconds to hh:mm:ss
  function convertHMS(value) {
    const sec = parseInt(value, 10); // convert value to number if it's string
    let hours = Math.floor(sec / 3600); // get hours
    let minutes = Math.floor((sec - hours * 3600) / 60); // get minutes
    let seconds = sec - hours * 3600 - minutes * 60; //  get seconds
    // add 0 if value < 10; Example: 2 => 02
    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return hours + ":" + minutes + ":" + seconds; // Return is HH : MM : SS
  }

  // Event duration in minutes
  const getDurationInMinutes = (measurement) => {
    if (measurement.startTime && measurement.stopValue) {
      const start = new Date(measurement.startTime);
      const stop = new Date(measurement.stopValue);
      const durationInMs = stop - start; // Duration in milliseconds
      return Math.floor(durationInMs / 60000); // Convert to minutes and round down
    }
    return null; // Return null if startTime or stopValue is not available
  };

  const getStartHour = (startTime) => {
    if (startTime !== null && startTime !== undefined) {
      const date = new Date(startTime);
      return date.getHours();
    } else {
      return null;
    }
  };

  const getStartDayOfWeek = (startTime) => {
    if (startTime !== null && startTime !== undefined) {
      const date = new Date(startTime);
      const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      return days[date.getDay()];
    } else {
      return null;
    }
  };

  const handleGraphTypeSelection = (type) => {
    setSelectedGraphType(type);
  };

  // Setting properties for current graphConfiguration
  const getGraphData = (graphId) => {
    var graphdataResults = [];
    let tempGraphData = [];
    var projectMeasurements = [];
    let tempTimeRangeDates = {};
    let graphDetails = [];

    let tempDataMeasurementRows = [];
    let tempFinancialMeasurementRows = [];

    let tempSelectedProjects = JSON.parse(JSON.stringify(selectedProject));

    dataService
      .getGraphConfigurationForViewingByUniqueId(graphId)
      .then((results) => {
        setGraphConfigurationName(results.name);
        setGraphConfigurationId(results.id);
        setGraphConfigurationUniqueId(results.uniqueId);
        setGraphConfigurationObject(results);
        // handleGraphConfiguration(JSON.parse(results.metaData));

        graphdataResults = results;
        if (graphdataResults !== null && graphdataResults !== undefined) {
          const parsedMeta = JSON.parse(results.metaData);
          setGraphProjects(graphdataResults.projects);
          const sortedBySequence = [...graphdataResults.projects].sort((a, b) => {
            const seqA = parsedMeta?.projects?.find((sp) => sp.projectId === a.id)?.sequence ?? 999;
            const seqB = parsedMeta?.projects?.find((sp) => sp.projectId === b.id)?.sequence ?? 999;
            return seqA - seqB;
          });
          setOrderedProjectNames(sortedBySequence.map((p) => p.resourceDescription));
          // Will be overwritten below once metaData is parsed (to respect saved order)
          graphdataResults.projects.forEach((project, index) => {
            project.details.forEach((detail) => {
              detail.resourceName = project.resourceDescription;
              graphDetails.push(detail);
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                detail.measurements.forEach((measurement) => {
                  measurement.registrationPointColor = registrationPointColors[index];
                  measurement.isProfit = detail.isProfit;
                });

                projectMeasurements.push(detail.measurements);
              }
            });
            setGraphDetails(graphDetails);
          });

          projectMeasurements = projectMeasurements.flat();
          let tempProjectMeasurementRows;

          tempProjectMeasurementRows = projectMeasurements.map((y) => ({
            accountId: y.accountId,
            accountName: y.accountName,
            accountCategoryId: y.accountCategoryId,
            accountCategoryName: y.accountCategoryName,
            checked: true,
            projectId: y.projectId,
            toDate: y.toDate,
            fromDate: y.fromDate,
            color: y.color,
            id: y.id,
            typeId: y.typeId,
            valueTypeId: y.valueTypeId,
            companyId: y.companyId,
            qrCode: y.qrCode,
            companyName: y.companyName,
            detailId: y.projectDetailId,
            projectName: y.projectName,
            resourceName: y.resourceName,
            registrationPointColor: y.registrationPointColor,
            description: y.description,
            durationInMinutes: getDurationInMinutes(y),
            startTime: y.startTime,
            startHour: getStartHour(y.startTime),
            startDayOfWeek: getStartDayOfWeek(y.startTime),
            stopTime: y.stopValue,
            insertedOn: new Date(y.insertedOn),
            updatedOn: y.stopTime,
            measurementSessionId: y.measurementSessionId,
            measurementValue: getMeasurementValue(
              y.typeId,
              y.externalApiTypeId,
              y.textValue,
              y.integerValue,
              y.dateValue,
              y.dataListValue,
              y.imageLink,
              y.externalApiValues,
              y.isNegative,
              y.numericValue
            ),
            integerValue: y.integerValue,
            running: y.running,
            thumbnail: y.thumbnail,
            imageId: y.imageId,
            commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
            commentImage: y.commentImage,
            commentId: y.commentId,
            valueType: getValueType(y.valueTypeId),
            keyValue: y.keyValue,
            eventPrice: y.eventPrice || 0,
            signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
            externalApiTypeId: y.externalApiTypeId,
            isProfit: y.isProfit,
            isNegative: y.isNegative,
            numericValue: y.numericValue,
            priceBundleId: y.priceBundleId,
            priceBundleName: y.priceBundleName,
            sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
            sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
          }));
          tempDataMeasurementRows.push(...tempProjectMeasurementRows.flat());

          tempFinancialMeasurementRows = tempDataMeasurementRows.filter((row) => row.eventPrice > 0);

          // Setting timerangedates
          let graphConfigurationMetaData = JSON.parse(results.metaData);
          if (graphConfigurationMetaData.dateRange !== null && graphConfigurationMetaData.dateRange !== undefined) {
            let dateRange = JSON.parse(results.metaData).dateRange;
            tempTimeRangeDates = { fromDate: new Date(dateRange.fromDate), startDate: new Date(dateRange.fromDate), toDate: new Date(dateRange.toDate), endDate: new Date(dateRange.toDate) };
          } else {
            let element = getLatestStartTimeElement(tempDataMeasurementRows);
            let from = timeRangeDates.fromDate;
            let to = timeRangeDates.toDate;
            if (element !== null && element !== undefined) {
              to = new Date(element.startTime);
              from = new Date(element.startTime);
              from.setHours(from.getHours() - 24);
              const formattedDate = from.toString();
              from = formattedDate;
              tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to };
            } else {
              tempTimeRangeDates = timeRangeDates;
            }
          }
          // Restore user-selected date range if previously changed
          if (extraParams.startDate && extraParams.endDate) {
            tempTimeRangeDates = {
              fromDate: new Date(extraParams.startDate),
              startDate: new Date(extraParams.startDate),
              toDate: new Date(extraParams.endDate),
              endDate: new Date(extraParams.endDate),
            };
          }

          graphdataResults.projects.forEach((project) => {
            tempSelectedProjects.push(project);
            tempGraphData.push(parseProjectData(project));
            project.details.forEach((detail) => {
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                projectMeasurements.push(detail.measurements);
              }
            });
          });

          // Setting the states for measurements and graph data
          const selectionPromise = new Promise((resolve) => {
            const indexes = tempDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            const profitLossIndexes = tempFinancialMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setSelection(indexes);
            setProfitLossSelection(profitLossIndexes);
            resolve();
          });

          const measurementRowsPromise = new Promise((resolve) => {
            setDataMeasurementRows([...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            setFinancialMeasurementRows(
              [...tempDataMeasurementRows]
                .flat()
                .sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn))
                .filter((row) => row.eventPrice > 0)
            );
            resolve();
          });

          const originalMeasurementRowsPromise = new Promise((resolve) => {
            const sortedRows = [...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn));
            setOriginalMeasurementRows(sortedRows);
            resetViewRangeToDefault(sortedRows);
            resolve();
          });

          // Data for yamazumi view
          const graphDataPromise = new Promise((resolve) => {
            setGraphData(parseMeasurementRows(tempDataMeasurementRows).flat());
            resolve();
          });

          // Measurements for display
          const measurementsToDisplayPromise = new Promise((resolve) => {
            setMeasurementsToDisplay(tempDataMeasurementRows.flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            resolve();
          });

          // Data for timeline view
          const chartRowsPromise = new Promise((resolve) => {
            let data = chartData(tempDataMeasurementRows.flat());
            setChartRows(data);
            resolve();
          });

          // Data for financial view
          const financialDataPromise = new Promise((resolve) => {
            constructPriceData(graphdataResults.projects);

            let financialChartMeasurements = tempDataMeasurementRows.sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn));

            let data = constructFinancialData(graphDetails, financialChartMeasurements);
            let detailNames = constructDetailCollection(graphDetails);
            setDetailsNames(detailNames);
            setFinancialChartData(data);

            // setting data for highlow table
            let tempGraphProjects = [...graphdataResults.projects]; // Clone the projects array

            tempGraphProjects = tempGraphProjects.map((project) => {
              return {
                ...project,
                details: project.details.map((detail) => {
                  return {
                    ...detail,
                    measurements: detail.measurements,
                  };
                }),
              };
            });
            setHighLowGraphProjects(tempGraphProjects);

            resolve();
          });

          var dateranges = { 0: tempTimeRangeDates };
          // Data for profit loss report
          const profitLossReportDataPromise = new Promise((resolve) => {
            let tempProfitLossReportData;
            formatProfitLossReportData(tempProjectMeasurementRows, dateranges).then((formattedData) => {
              tempProfitLossReportData = formattedData;
              setProfitLossReportData(tempProfitLossReportData);
            });

            resolve();
          });

          const timeRangeDatesPromise = new Promise((resolve) => {
            setTimeRangeDates(tempTimeRangeDates);

            // add sequence to tempTimeRangeDates
            let tempProfitLossDates = tempTimeRangeDates;
            tempProfitLossDates.sequence = 1;
            setProfitLossDates({ 0: tempProfitLossDates });
            resolve();
          });

          const selectedProjectsPromise = new Promise((resolve) => {
            setSelectedProjects(tempSelectedProjects);
            resolve();
          });

          // Wait for all state updates to complete before setting loading to false
          Promise.all([
            selectionPromise,
            measurementRowsPromise,
            originalMeasurementRowsPromise,
            graphDataPromise,
            measurementsToDisplayPromise,
            chartRowsPromise,
            financialDataPromise,
            profitLossReportDataPromise,
            timeRangeDatesPromise,
            selectedProjectsPromise,
          ]).then(() => {
            setLoading(false); // Only set loading to false after all updates
            
            // Fetch recurring financials after graph data is loaded
            fetchRecurringFinancials(tempDataMeasurementRows, tempTimeRangeDates);
          });
        }
      })
      .catch((error) => {
        console.log("Error ", error);
        setNoGraphFound(true);
        toast.error("Something went wrong", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch recurring financials for all companies in the graph
  const fetchRecurringFinancials = async (measurementRows, timeRangeDates) => {
    try {
      const companyIds = [...new Set(measurementRows.map(row => row.companyId))];
      
      if (companyIds.length === 0 || !timeRangeDates.fromDate || !timeRangeDates.toDate) {
        setRecurringFinancials([]);
        return;
      }

      const result = await dataService.getRecurringFinancialsByCompanyIdsAndDateRange(
        companyIds,
        timeRangeDates.fromDate.toISOString(),
        timeRangeDates.toDate.toISOString()
      );

      setRecurringFinancials(result || []);
    } catch (error) {
      console.error("Error fetching recurring financials:", error);
      setRecurringFinancials([]);
    }
  };

  const handleComparePeriod = async (dateRange) => {
    // Validate inputs
    if (!dateRange?.fromDate || !dateRange?.toDate) {
      console.warn("Invalid date range provided");
      return;
    }

    const fromDate = new Date(dateRange.fromDate);
    const toDate = new Date(dateRange.toDate);

    // Validate date objects
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      console.warn("Invalid dates in range");
      return;
    }

    const periodDurationMs = toDate - fromDate;

    // Ensure positive duration
    if (periodDurationMs <= 0) {
      console.warn("Invalid date range: end date must be after start date");
      return;
    }

    const previousDateRange = {
      fromDate: new Date(fromDate.getTime() - periodDurationMs),
      toDate: fromDate, // Previous period ends where current period starts
    };

    // 2. Fetch data for the previous period
    try {
      var graphdataResults = [];
      var projectMeasurements = [];
      let tempDataMeasurementRows = [];

      await dataService
        .getGraphConfigurationForViewingByUniqueIdAndDateRange(graphId, previousDateRange.fromDate.toISOString(), previousDateRange.toDate.toISOString())
        .then((results) => {
          graphdataResults = results;
          if (graphdataResults !== null && graphdataResults !== undefined) {
            graphdataResults.projects.forEach((project, index) => {
              project.details.forEach((detail) => {
                if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                  detail.measurements.forEach((measurement) => {
                    measurement.registrationPointColor = registrationPointColors[index];
                    measurement.isProfit = detail.isProfit;
                  });

                  projectMeasurements.push(detail.measurements);
                }
              });
            });

            projectMeasurements = projectMeasurements.flat();
            let tempProjectMeasurementRows;

            tempProjectMeasurementRows = projectMeasurements.map((y) => ({
              accountId: y.accountId,
              accountName: y.accountName,
              accountCategoryId: y.accountCategoryId,
              accountCategoryName: y.accountCategoryName,
              checked: true,
              projectId: y.projectId,
              toDate: y.toDate,
              fromDate: y.fromDate,
              color: y.color,
              id: y.id,
              typeId: y.typeId,
              valueTypeId: y.valueTypeId,
              companyId: y.companyId,
              qrCode: y.qrCode,
              companyName: y.companyName,
              detailId: y.projectDetailId,
              projectName: y.projectName,
              resourceName: y.resourceName,
              registrationPointColor: y.registrationPointColor,
              description: y.description,
              startTime: y.startTime,
              startHour: getStartHour(y.startTime),
              durationInMinutes: getDurationInMinutes(y),
              startDayOfWeek: getStartDayOfWeek(y.startTime),
              stopTime: y.stopValue,
              insertedOn: new Date(y.insertedOn),
              updatedOn: y.stopTime,
              measurementSessionId: y.measurementSessionId,
              measurementValue: getMeasurementValue(
                y.typeId,
                y.externalApiTypeId,
                y.textValue,
                y.integerValue,
                y.dateValue,
                y.dataListValue,
                y.imageLink,
                y.externalApiValues,
                y.isNegative,
                y.numericValue
              ),
              integerValue: y.integerValue,
              running: y.running,
              thumbnail: y.thumbnail,
              imageId: y.imageId,
              commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
              commentImage: y.commentImage,
              commentId: y.commentId,
              valueType: getValueType(y.valueTypeId),
              keyValue: y.keyValue,
              eventPrice: y.eventPrice || 0,
              signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
              externalApiTypeId: y.externalApiTypeId,
              isProfit: y.isProfit,
              isNegative: y.isNegative,
              numericValue: y.numericValue,
              priceBundleId: y.priceBundleId,
              priceBundleName: y.priceBundleName,
              sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
              sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
            }));
            tempDataMeasurementRows.push(...tempProjectMeasurementRows.flat());

            setOriginalComparePeriodMeasurements([...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            let filteredDataMeasurementRows = filterDataMeasurements(tempDataMeasurementRows); // Filtered data measurements (active filters are applied)

            graphdataResults.projects.forEach((project) => {
              project.details.forEach((detail) => {
                if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                  projectMeasurements.push(detail.measurements);
                }
              });
            });

            // Measurements for display
            const measurementsToDisplayPromise = new Promise((resolve) => {
              setComparePeriodMeasurements(filteredDataMeasurementRows.flat());
              resolve();
            });

            // Wait for all state updates to complete before setting loading to false
            Promise.all([measurementsToDisplayPromise]).then(() => {
              setFetchingData(false); // Set fetchingData to false when done
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching graph configuration by date range:", error);
        })
        .finally(() => {
          setFetchingData(false); // Set fetchingData to false when done
        });

      // handle previous period data like normal data
    } catch (error) {
      console.error("Error fetching previous period data:", error);
    }
  };

  const fetchConfigurationByDateRange = (dateRange) => {
    var graphdataResults = [];
    let tempGraphData = [];
    var projectMeasurements = [];
    let graphDetails = [];

    let tempDataMeasurementRows = [];

    let tempSelectedProjects = JSON.parse(JSON.stringify(selectedProject));

    setFetchingData(true);
    dataService
      .getGraphConfigurationForViewingByUniqueIdAndDateRange(graphId, dateRange.fromDate.toISOString(), dateRange.toDate.toISOString())
      .then((results) => {
        setGraphConfigurationObject(results);

        graphdataResults = results;
        if (graphdataResults !== null && graphdataResults !== undefined) {
          setGraphProjects(graphdataResults.projects);
          graphdataResults.projects.forEach((project, index) => {
            project.details.forEach((detail) => {
              graphDetails.push(detail);
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                detail.measurements.forEach((measurement) => {
                  measurement.registrationPointColor = registrationPointColors[index];
                  measurement.isProfit = detail.isProfit;
                });

                projectMeasurements.push(detail.measurements);
              }
            });
            setGraphDetails(graphDetails);
          });

          projectMeasurements = projectMeasurements.flat();
          let tempProjectMeasurementRows;

          tempProjectMeasurementRows = projectMeasurements.map((y) => ({
            accountId: y.accountId,
            accountName: y.accountName,
            accountCategoryId: y.accountCategoryId,
            accountCategoryName: y.accountCategoryName,
            checked: true,
            projectId: y.projectId,
            toDate: y.toDate,
            fromDate: y.fromDate,
            color: y.color,
            id: y.id,
            typeId: y.typeId,
            valueTypeId: y.valueTypeId,
            companyId: y.companyId,
            qrCode: y.qrCode,
            companyName: y.companyName,
            detailId: y.projectDetailId,
            projectName: y.projectName,
            resourceName: y.resourceName,
            registrationPointColor: y.registrationPointColor,
            description: y.description,
            durationInMinutes: getDurationInMinutes(y),
            startTime: y.startTime,
            startHour: getStartHour(y.startTime),
            startDayOfWeek: getStartDayOfWeek(y.startTime),
            stopTime: y.stopValue,
            insertedOn: new Date(y.insertedOn),
            updatedOn: y.stopTime,
            measurementSessionId: y.measurementSessionId,
            measurementValue: getMeasurementValue(
              y.typeId,
              y.externalApiTypeId,
              y.textValue,
              y.integerValue,
              y.dateValue,
              y.dataListValue,
              y.imageLink,
              y.externalApiValues,
              y.isNegative,
              y.numericValue
            ),
            integerValue: y.integerValue,
            running: y.running,
            thumbnail: y.thumbnail,
            imageId: y.imageId,
            commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
            commentImage: y.commentImage,
            commentId: y.commentId,
            valueType: getValueType(y.valueTypeId),
            keyValue: y.keyValue,
            eventPrice: y.eventPrice || 0,
            signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
            externalApiTypeId: y.externalApiTypeId,
            isProfit: y.isProfit,
            isNegative: y.isNegative,
            numericValue: y.numericValue,
            priceBundleId: y.priceBundleId,
            priceBundleName: y.priceBundleName,
            sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
            sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
          }));
          tempDataMeasurementRows.push(...tempProjectMeasurementRows.flat());

          // tempFinancialMeasurementRows = tempDataMeasurementRows.filter((row) => row.eventPrice > 0);

          let filteredDataMeasurementRows = filterDataMeasurements(tempDataMeasurementRows); // Filtered data measurements (active filters are applied)
          let filteredFinancialMeasurementRows = filterFinancialMeasurements(tempDataMeasurementRows.filter((row) => row.eventPrice > 0)); // Filtered financial measurements (active filters are applied)

          graphdataResults.projects.forEach((project) => {
            tempSelectedProjects.push(project);
            tempGraphData.push(parseProjectData(project));
            project.details.forEach((detail) => {
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                projectMeasurements.push(detail.measurements);
              }
            });
          });

          // Setting the states for measurements and graph data
          const selectionPromise = new Promise((resolve) => {
            const indexes = filteredDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            const profitLossIndexes = filteredFinancialMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setSelection(indexes);
            setProfitLossSelection(profitLossIndexes);
            resolve();
          });

          const measurementRowsPromise = new Promise((resolve) => {
            setDataMeasurementRows([...filteredDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            setFinancialMeasurementRows([...filteredDataMeasurementRows].flat());
            resolve();
          });

          const originalMeasurementRowsPromise = new Promise((resolve) => {
            const sortedRows = [...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn));
            setOriginalMeasurementRows(sortedRows);
            resetViewRangeToDefault(sortedRows);
            resolve();
          });

          // Data for yamazumi view
          const graphDataPromise = new Promise((resolve) => {
            setGraphData(parseMeasurementRows(filteredDataMeasurementRows).flat());
            resolve();
          });

          // Measurements for display
          const measurementsToDisplayPromise = new Promise((resolve) => {
            setMeasurementsToDisplay(filteredDataMeasurementRows.flat());
            resolve();
          });

          // Data for timeline view
          const chartRowsPromise = new Promise((resolve) => {
            let data = chartData(filteredDataMeasurementRows.flat());

            setChartRows(data);
            resolve();
          });

          // Data for financial view
          const financialDataPromise = new Promise((resolve) => {
            constructPriceData(graphdataResults.projects);

            let financialChartMeasurements = filteredDataMeasurementRows;

            let data = constructFinancialData(graphDetails, financialChartMeasurements);
            let detailNames = constructDetailCollection(graphDetails);
            setDetailsNames(detailNames);
            setFinancialChartData(data);

            // setting data for highlow table
            let tempGraphProjects = [...graphdataResults.projects]; // Clone the projects array

            tempGraphProjects = tempGraphProjects.map((project) => {
              return {
                ...project,
                details: project.details.map((detail) => {
                  return {
                    ...detail,
                    measurements: detail.measurements,
                  };
                }),
              };
            });
            setHighLowGraphProjects(tempGraphProjects);

            resolve();
          });

          var dateranges = { 0: dateRange };
          // Data for profit loss report
          const profitLossReportDataPromise = new Promise((resolve) => {
            let tempProfitLossReportData;
            formatProfitLossReportData(tempProjectMeasurementRows, dateranges).then((formattedData) => {
              tempProfitLossReportData = formattedData;
              setProfitLossReportData(tempProfitLossReportData);
            });

            resolve();
          });

          const selectedProjectsPromise = new Promise((resolve) => {
            setSelectedProjects(tempSelectedProjects);
            resolve();
          });

          // Wait for all state updates to complete before setting loading to false
          Promise.all([
            selectionPromise,
            measurementRowsPromise,
            originalMeasurementRowsPromise,
            graphDataPromise,
            measurementsToDisplayPromise,
            chartRowsPromise,
            financialDataPromise,
            profitLossReportDataPromise,
            selectedProjectsPromise,
          ]).then(() => {
            setFetchingData(false); // Only set loading to false after all updates
            
            // Fetch recurring financials after date range data is loaded
            fetchRecurringFinancials(tempDataMeasurementRows, dateRange);
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching graph configuration by date range:", error);
      })
      .finally(() => {
        setFetchingData(false);
      });
  };

  const getValueType = (id) => {
    switch (id) {
      case valueTypeCodes.VA:
        return "Value added";
      case valueTypeCodes.NVA:
        return "No value added";
      case valueTypeCodes.W:
        return "Waste";
      default:
        return "Unknown";
    }
  };

  const getMeasurementValue = (typeId, externalApiTypeId, text, int, time, dataListValue, imageLink, externalApiValue, isNegative, numericValue) => {
    switch (typeId) {
      case 1:
        return int;
      case 2:
        if (int > 0) {
          return time;
        } else {
          return "";
        }
      case 3:
        return text;
      case 4:
        return dataListValue;
      case 5:
        return text;
      case 6:
        return imageLink;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          if (int > 0) {
            return time;
          } else {
            return "";
          }
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          if (isNegative) {
            return `-${numericValue}`;
          } else {
            return numericValue;
          }
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return text;
        }
        return text;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getCommentText = (typeId, externalApiTypeId, text, commentText) => {
    switch (typeId) {
      case 1:
        return commentText;
      case 2:
        return commentText;
      case 3:
        return commentText;
      case 4:
        return commentText;
      case 5:
        return commentText;
      case 6:
        return commentText;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return commentText;
        }
        return commentText;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getLatestStartTimeElement = (data) => {
    //We check the latest measurement -> this is how we will decide from where to start the 'last 24h'
    if (data !== null && data.length > 0) {
      return data.reduce((latest, current) => {
        const latestTime = new Date(latest.startTime);
        const currentTime = new Date(current.startTime);

        // Check if current startTime is later than the latest
        return currentTime > latestTime ? current : latest;
      });
    } else {
      return null;
    }
  };

  const zoomMsMap = { "1 Day": 86400000, "1 Week": 7 * 86400000, "1 Month": 30 * 86400000, "3 Months": 90 * 86400000 };

  const resetViewRangeToDefault = (rows) => {
    if (extraParams.viewFrom && extraParams.viewTo) {
      setViewRangeDates({ fromDate: new Date(extraParams.viewFrom), endDate: new Date(extraParams.viewTo) });
      setTimeRangeZoom(extraParams.zoom || '1 Week');
      return;
    }
    try {
      const s = JSON.parse(localStorage.getItem(viewRangeKey));
      if (s?.viewFrom && s?.viewTo) {
        setViewRangeDates({ fromDate: new Date(s.viewFrom), endDate: new Date(s.viewTo) });
        setTimeRangeZoom(s.zoom || '1 Week');
        return;
      }
    } catch {}
    const latest = getLatestStartTimeElement(rows ?? originalMeasurementRows);
    const end = latest ? new Date(latest.startTime) : new Date();
    const from = new Date(end.getTime() - 7 * 86400000);
    setViewRangeDates({ fromDate: from, endDate: end });
    setTimeRangeZoom("1 Week");
  };

  const handleTimeRangeZoomChange = (zoom) => {
    setTimeRangeZoom(zoom);
    const duration = zoomMsMap[zoom] ?? 7 * 86400000;
    const latest = getLatestStartTimeElement(originalMeasurementRows);
    const end = latest ? new Date(latest.startTime) : (viewRangeDates.endDate ?? new Date());
    const from = new Date(end.getTime() - duration);
    setViewRangeDates({ fromDate: from, endDate: end });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom, viewFrom: from.toISOString(), viewTo: end.toISOString() })); } catch {}
    setExtraParam('zoom', zoom);
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', end.toISOString());
  };

  const handleTimeRangeScrollPrev = () => {
    const duration = zoomMsMap[timeRangeZoom] ?? 7 * 86400000;
    let from = new Date(viewRangeDates.fromDate.getTime() - duration);
    let to = new Date(viewRangeDates.endDate.getTime() - duration);
    if (timeRangeDates.fromDate && from < timeRangeDates.fromDate) {
      const overflow = timeRangeDates.fromDate.getTime() - from.getTime();
      from = new Date(timeRangeDates.fromDate);
      to = new Date(to.getTime() + overflow);
    }
    setViewRangeDates({ fromDate: from, endDate: to });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom: timeRangeZoom, viewFrom: from.toISOString(), viewTo: to.toISOString() })); } catch {}
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', to.toISOString());
  };

  const handleTimeRangeScrollNext = () => {
    const duration = zoomMsMap[timeRangeZoom] ?? 7 * 86400000;
    let from = new Date(viewRangeDates.fromDate.getTime() + duration);
    let to = new Date(viewRangeDates.endDate.getTime() + duration);
    if (timeRangeDates.toDate && to > timeRangeDates.toDate) {
      const overflow = to.getTime() - timeRangeDates.toDate.getTime();
      to = new Date(timeRangeDates.toDate);
      from = new Date(from.getTime() - overflow);
    }
    setViewRangeDates({ fromDate: from, endDate: to });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom: timeRangeZoom, viewFrom: from.toISOString(), viewTo: to.toISOString() })); } catch {}
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', to.toISOString());
  };

  const getMeasurementTime = (m) => {
    if (m.startTime) { const t = new Date(m.startTime).getTime(); if (!isNaN(t)) return t; }
    if (m.insertedOn) { const t = new Date(m.insertedOn).getTime(); if (!isNaN(t)) return t; }
    return null;
  };

  const filterByViewRange = (rows) => {
    if (!viewRangeDates?.fromDate || !viewRangeDates?.endDate) return rows;
    const from = viewRangeDates.fromDate.getTime();
    const to = viewRangeDates.endDate.getTime();
    return rows.filter((m) => {
      const t = getMeasurementTime(m);
      return t === null || (t >= from && t <= to);
    });
  };

  const setDateRange = (startDate, endDate) => {
    // check if both dates are valid
    if (startDate === null || startDate === undefined || endDate === null || endDate === undefined) {
      console.warn("Invalid date range provided. Both startDate and endDate must be valid dates.");
      return;
    } else if (!(startDate instanceof Date) || !(endDate instanceof Date) || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("Invalid date range provided. Both startDate and endDate must be valid Date objects.");
      return;
    }

    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setTimeRangeDates({ fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) });
      setExtraParam('startDate', new Date(startDate).toISOString());
      setExtraParam('endDate', new Date(endDate).toISOString());
      let tempProfitLossDates = { 0: { sequence: 1, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) } };
      setProfitLossDates(tempProfitLossDates);

      // new daterange
      let newDateRange = { sequence: 1, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) };

      fetchConfigurationByDateRange(newDateRange);
    } else {
      resetDateRangeFilter();
    }
  };

  const handleSetStartDate = (event) => {
    if (event !== startDate) {
      setStartDate(event);
      setDateRange(event, endDate);
    }
  };
  const handleSetEndDate = (event) => {
    if (event !== endDate) {
      setEndDate(event);
      setDateRange(startDate, event);
    }
  };

  const resetDateRangeFilter = () => {
    // First check if there's a saved date range in the graph configuration
    if (graphConfigurationObject && graphConfigurationObject.metaData) {
      const graphConfigurationMetaData = JSON.parse(graphConfigurationObject.metaData);
      if (graphConfigurationMetaData.dateRange !== null && graphConfigurationMetaData.dateRange !== undefined) {
        const savedFrom = new Date(graphConfigurationMetaData.dateRange.fromDate);
        const savedTo = new Date(graphConfigurationMetaData.dateRange.toDate);
        const tempTimeRangeDates = { fromDate: savedFrom, startDate: savedFrom, toDate: savedTo, endDate: savedTo, sequence: 1 };
        let tempProfitLossDates = { 0: tempTimeRangeDates };
        setStartDate(savedFrom);
        setEndDate(savedTo);
        setTimeRangeDates(tempTimeRangeDates);
        setProfitLossDates(tempProfitLossDates);
        setExtraParam('startDate', '');
        setExtraParam('endDate', '');

        // new date
        let newDateRange = { fromDate: savedFrom, startDate: savedFrom, toDate: savedTo, endDate: savedTo, sequence: 1 };
        fetchConfigurationByDateRange(newDateRange);
        return;
      }
    }

    // If no saved date range, fall back to 24 hours before last measurement
    let element = getLatestStartTimeElement(originalMeasurementRows);
    let from = timeRangeDates.fromDate;
    let to = timeRangeDates.toDate;
    let tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };

    if (element !== null && element !== undefined) {
      to = new Date(element.startTime);
      from = new Date(element.startTime);
      from.setHours(from.getHours() - 24);
      const formattedDate = from.toString();
      from = formattedDate;
      tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };
      let tempProfitLossDates = { 0: tempTimeRangeDates };
      setStartDate(new Date(from));
      setEndDate(new Date(to));
      setTimeRangeDates(tempTimeRangeDates);
      setProfitLossDates(tempProfitLossDates);
      setExtraParam('startDate', '');
      setExtraParam('endDate', '');

      // new date
      let newDateRange = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };
      fetchConfigurationByDateRange(newDateRange);
    }

    setPercentageVisible(false);
  };

  // Update Timerange for data
  const updateDataRange = (data, changeDay) => {
    let tempStartDate = new Date(data[0]);
    let tempEndDate = new Date(data[1]);
    let duplicateDateRange = JSON.parse(JSON.stringify(timeRangeDates));

    var objNewTimeRangeDate;

    if (changeDay) {
      // if it was a 'changeDay' action we start again with a 24h range
      objNewTimeRangeDate = { startDate: new Date(tempStartDate), fromDate: new Date(tempStartDate), toDate: new Date(tempEndDate), endDate: new Date(tempEndDate) };
    } else {
      // if slider was changed
      objNewTimeRangeDate = { startDate: new Date(tempStartDate), fromDate: new Date(duplicateDateRange.fromDate), toDate: new Date(duplicateDateRange.toDate), endDate: new Date(tempEndDate) };
    }

    if (JSON.stringify(objNewTimeRangeDate) !== JSON.stringify(timeRangeDates)) {
      setTimeRangeDates(objNewTimeRangeDate);
      setExtraParam('startDate', objNewTimeRangeDate.fromDate.toISOString());
      setExtraParam('endDate', objNewTimeRangeDate.toDate.toISOString());
      let tempProfitLossDates = objNewTimeRangeDate;
      tempProfitLossDates.sequence = 1;
      setProfitLossDates({ 0: tempProfitLossDates });

      onFiltersChange(null, objNewTimeRangeDate, { 0: tempProfitLossDates }, null);
    }
  };

  const getColorForType = (id) => {
    switch (id) {
      case valueTypeCodes.VA:
        return "#4caf50"; // Green for Value Added
      case valueTypeCodes.NVA:
        return "#ff9800"; // Orange for Non Value Added
      case valueTypeCodes.W:
        return "#f44336"; // Red for Waste
      default:
        return "#9e9e9e"; // Gray for Unknown
    }
  };

  const parseMeasurementRows = (measurementRows) => {
    // Initialize an object to hold the parsed data for each project
    let projectData = {};
    // Loop through each measurement row in the array
    measurementRows.forEach((row) => {
      // Only process rows where "checked" is true
      if (!row.checked || row.stopTime === null) return;

      if (row.typeId === controlTypes.STOPWATCH || (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.STOPWATCH)) {
        const projectId = row.projectId;
        const detailId = row.detailId;
        const startTime = new Date(row.startTime);
        const stopTime = new Date(row.stopTime);
        const duration = (stopTime - startTime) / 3600000; // Calculate duration in hours
        const eventPrice = row.eventPrice;

        // Initialize data for this project if not already done
        if (!projectData[projectId]) {
          projectData[projectId] = {
            state: row.projectName + " - " + row.resourceName,
            values: {},
          };
        }

        // Check if this detailId has already been processed for this project
        if (!projectData[projectId].values[detailId]) {
          projectData[projectId].values[detailId] = {
            typeId: row.valueTypeId,
            title: row.description,
            color: row.color,
            typeName: row.valueTypeId === valueTypeCodes.VA ? "VA" : row.valueTypeId === valueTypeCodes.NVA ? "NVA" : row.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN",
            totalDuration: 0,
            measurementCounter: 0,
            totalPrice: 0,
          };
        }

        if (duration > 0) {
          // Update the total duration and measurement counter for this detail
          projectData[projectId].values[detailId].totalPrice += eventPrice;
          projectData[projectId].values[detailId].totalDuration += duration;
          projectData[projectId].values[detailId].measurementCounter += 1;
        }
      }
    });

    // Transform the data into the required output format
    const parsedOutput = [];
    Object.values(projectData).forEach((project) => {
      Object.values(project.values).forEach((detail) => {
        parsedOutput.push({
          state: project.state,
          [detail.typeName]: detail.totalDuration, // Dynamic key based on typeName
          type: detail.typeId,
          typeName: detail.typeName,
          title: detail.title,
          color: detail.color,
          measurements: detail.measurementCounter,
          totalPrice: detail.totalPrice,
        });
      });
    });

    return parsedOutput;
  };

  const formatProfitLossReportData = async (measurements, dateranges) => {
    // Merge recurring financials if toggle is on
    if (showRecurringPrices && recurringFinancials.length > 0) {
      measurements = [...measurements, ...recurringFinancials];
    }

    let accounts = [];
    let accountCategories = [];
    let tempProfitLossReportData = {
      companies: [],
      revenueAccountCategoryResults: [],
      costAccountCategoryResults: [],
      totalProfit: {},
      revenueTotal: {}, // Added for overall revenue totals
      costTotal: {}, // Added for overall cost totals
      percentageTotalProfit: {},
    };

    // Fetch accounts and categories if not already loaded
    if (!allAccounts.length && !allAccountCategories.length) {
      try {
        const [fetchedAccounts] = await Promise.all([
          dataService.getAllAccountsForGraphConfigurationByUniqueId(graphId),
        ]);

        let tempAccounts = [];
        let tempAccountCategories = [];

        fetchedAccounts.forEach((company) => {
          company.accountCategories.forEach((category) => {
            let tempCategory = {
              id: category.id,
              name: category.name,
              isRevenue: category.isRevenue,
              isActive: category.isActive,
              companyId: category.companyId,
              companyName: company.name,
              accounts: category.accounts,
            };
            tempAccountCategories.push(tempCategory);

            category.accounts.forEach((account) => {
              // Check if account already exists in tempAccounts
              let existingAccount = tempAccounts.find((acc) => acc.id === account.id);
              if (!existingAccount) {
                tempAccounts.push(account);
              }
            });
          });
        });

        setAllAccounts(tempAccounts);
        setAllAccountCategories(tempAccountCategories);

        accounts = tempAccounts;
        accountCategories = tempAccountCategories;
      } catch (error) {
        console.error("Error fetching account data:", error);
        return tempProfitLossReportData;
      }
    } else {
      accounts = allAccounts;
      accountCategories = allAccountCategories;
    }

    // sort dateranges by sequence
    let orderedDateRanges = Object.keys(dateranges)
      .map((key) => dateranges[key])
      .sort((a, b) => a.sequence - b.sequence);

    // Initialize date range totals and process measurements
    Object.keys(orderedDateRanges).forEach((rangeIndex) => {
      const { fromDate, toDate } = orderedDateRanges[rangeIndex];
      const from = new Date(fromDate);
      const to = new Date(toDate);

      // Initialize date range totals if not already initialized
      if (!tempProfitLossReportData.totalProfit[rangeIndex]) {
        tempProfitLossReportData.totalProfit[rangeIndex] = 0;
      }
      if (!tempProfitLossReportData.revenueTotal[rangeIndex]) {
        tempProfitLossReportData.revenueTotal[rangeIndex] = 0;
      }
      if (!tempProfitLossReportData.costTotal[rangeIndex]) {
        tempProfitLossReportData.costTotal[rangeIndex] = 0;
      }

      // Filter measurements within the current date range
      const measurementsInDateRange = measurements.filter((measurement) => {
        const insertedDate = roundToSecond(new Date(measurement.insertedOn));
        return insertedDate > from && insertedDate <= to;
      });

      // Process each measurement within the current date range
      measurementsInDateRange
        .filter((x) => x.accountCategoryId !== null)
        .forEach((measurement) => {
          let measurementAccount = accounts.find((acc) => acc.id === measurement.accountId);
          if (!measurementAccount) return;
          let measurementAccountCategory = accountCategories.find((category) => category.id === measurementAccount.accountCategoryId);

          if (!measurementAccountCategory) return;

          let measurementCompanyName = measurement.companyName;

          // Add company if not already present
          if (!tempProfitLossReportData.companies.includes(measurementCompanyName)) {
            tempProfitLossReportData.companies.push(measurementCompanyName);
            tempProfitLossReportData.companies.sort((a, b) => a.localeCompare(b));
          }

          const isRevenue = measurementAccountCategory.isRevenue;
          const categoryList = isRevenue ? tempProfitLossReportData.revenueAccountCategoryResults : tempProfitLossReportData.costAccountCategoryResults;

          let category = categoryList.find((cat) => cat.id === measurementAccountCategory.id);

          // Add category if not already present
          if (!category) {
            category = {
              id: measurementAccountCategory.id,
              name: measurementAccountCategory.name,
              isRevenue: isRevenue,
              accountResults: [],
              total: {}, // Dynamically calculated totals for each date range
              percentage: {},
            };
            categoryList.push(category);
          }

          let account = category.accountResults.find((acc) => acc.id === measurementAccount.id);

          // Add account if not already present
          if (!account) {
            account = {
              id: measurementAccount.id,
              name: measurementAccount.name,
              isActive: measurementAccount.isActive,
              total: {},
              percentage: {},
            };
            category.accountResults.push(account);
          }

          // Update totals
          account.total[rangeIndex] = (account.total[rangeIndex] || 0) + (measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);
          category.total[rangeIndex] = (category.total[rangeIndex] || 0) + (measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);

          if (isRevenue) {
            tempProfitLossReportData.revenueTotal[rangeIndex] += measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice;
          } else {
            tempProfitLossReportData.costTotal[rangeIndex] += measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice;
          }

          tempProfitLossReportData.totalProfit[rangeIndex] =
            (tempProfitLossReportData.totalProfit[rangeIndex] || 0) + (isRevenue === measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);
        });

      // Ensure totals for each category and account have all date ranges
      tempProfitLossReportData.revenueAccountCategoryResults.forEach((category) => {
        Object.keys(orderedDateRanges).forEach((rangeIndex) => {
          if (!category.total[rangeIndex]) {
            category.total[rangeIndex] = 0; // Set missing total to 0
          }
          category.accountResults.forEach((account) => {
            if (!account.total[rangeIndex]) {
              account.total[rangeIndex] = 0; // Set missing account total to 0
            }
          });
        });
      });

      tempProfitLossReportData.costAccountCategoryResults.forEach((category) => {
        Object.keys(orderedDateRanges).forEach((rangeIndex) => {
          if (!category.total[rangeIndex]) {
            category.total[rangeIndex] = 0; // Set missing total to 0
          }
          category.accountResults.forEach((account) => {
            if (!account.total[rangeIndex]) {
              account.total[rangeIndex] = 0; // Set missing account total to 0
            }
          });
        });
      });

      // Ensure overall totals for revenue and cost have all date ranges
      Object.keys(orderedDateRanges).forEach((rangeIndex) => {
        if (!tempProfitLossReportData.revenueTotal[rangeIndex]) {
          tempProfitLossReportData.revenueTotal[rangeIndex] = 0;
        }
        if (!tempProfitLossReportData.costTotal[rangeIndex]) {
          tempProfitLossReportData.costTotal[rangeIndex] = 0;
        }
      });
    });

    // Add percentage calculations for each category and account
    const calculatePercentage = (prev, curr) => {
      if (prev === 0 && curr === 0) return 0; // No change
      if (prev === 0) return 100; // From 0 to any value
      return ((curr - prev) / Math.abs(prev)) * 100;
    };

    // Calculate percentages for each category
    [tempProfitLossReportData.revenueAccountCategoryResults, tempProfitLossReportData.costAccountCategoryResults].forEach((categoryList) => {
      categoryList.forEach((category) => {
        Object.keys(category.total).forEach((rangeIndex, idx, rangeKeys) => {
          if (idx > 0) {
            const prevIndex = rangeKeys[idx - 1];
            category.percentage[rangeIndex] = calculatePercentage(category.total[prevIndex] || 0, category.total[rangeIndex] || 0);
          }
        });

        category.accountResults.forEach((account) => {
          Object.keys(account.total).forEach((rangeIndex, idx, rangeKeys) => {
            if (idx > 0) {
              const prevIndex = rangeKeys[idx - 1];
              account.percentage[rangeIndex] = calculatePercentage(account.total[prevIndex] || 0, account.total[rangeIndex] || 0);
            }
          });
        });
      });
    });

    // Calculate percentages for totalProfit
    tempProfitLossReportData.percentageTotalProfit = {};
    Object.keys(tempProfitLossReportData.totalProfit).forEach((rangeIndex, idx, rangeKeys) => {
      if (idx > 0) {
        const prevIndex = rangeKeys[idx - 1];
        tempProfitLossReportData.percentageTotalProfit[rangeIndex] = calculatePercentage(tempProfitLossReportData.totalProfit[prevIndex] || 0, tempProfitLossReportData.totalProfit[rangeIndex] || 0);
      }
    });

    return tempProfitLossReportData;
  };

  const parseProjectData = (project) => {
    if (project) {
      const projectName = project.projectName;
      var projectValues = [];
      let dataSource = {};

      project.details.forEach((detail) => {
        if ((detail.controlTypeId === controlTypes.STOPWATCH || (detail.controlTypeId === controlTypes.EXTERNALAPI && detail.externalApiTypeId === externalApiTypes.STOPWATCH)) && detail.isActive) {
          let totalDuration = 0;
          var measurementCounter = 0;

          detail.measurements.forEach((measurement) => {
            if (measurement.stopValue !== null && measurement.stopValue !== undefined) {
              const start = roundToSecond(new Date(measurement.startTime));
              const stop = roundToSecond(new Date(measurement.stopValue));

              // Calculate duration in hours
              const duration = (stop - start) / 3600000; // 3600000 ms = 1 hour
              totalDuration += duration;
              measurementCounter += 1;
            }
          });

          dataSource[detail.description] = totalDuration;

          projectValues.push({
            projectId: project.id,
            state: projectName + " - " + project.resourceDescription,
            [detail.valueTypeId === valueTypeCodes.VA ? "VA" : detail.valueTypeId === valueTypeCodes.NVA ? "NVA" : detail.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN"]: totalDuration, // Dynamic key
            type: detail.valueTypeId,
            typeName: detail.valueTypeId === valueTypeCodes.VA ? "VA" : detail.valueTypeId === valueTypeCodes.NVA ? "NVA" : detail.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN",
            title: detail.description,
            color: getColorForType(detail.valueTypeId),
            measurements: measurementCounter,
          });
        }
      });

      return projectValues;
    }
  };

  const getEmptyStateTitle = () => {
    if (selectedProjects.length === 0) {
      return "No projects selected";
    } else {
      return "No measurements";
    }
  };

  const getEmptyStateSubtitle = () => {
    if (selectedProjects.length === 0) {
      return "Select at least 1 project to display data in the Yamazumi graph.";
    } else {
      return "There are no measurements to display";
    }
  };

  //DevEx functions
  const onSelectionChange = (e) => {
    var tempRows = JSON.parse(JSON.stringify(dataMeasurementRows));
    const selectedIds = e.map((index) => (tempRows[index] !== undefined ? tempRows[index].id : null));
    setSelection(e);
    const updatedArray = tempRows.map((item) => (selectedIds.includes(item.id) ? { ...item, checked: true } : { ...item, checked: false }));

    var data = chartData(updatedArray.filter((x) => x.checked));
    if (JSON.parse(JSON.stringify(data)) !== JSON.parse(JSON.stringify(chartRows))) {
      setChartRows(data);
    }

    let tempProfitLossReportData;
    formatProfitLossReportData(
      updatedArray.filter((x) => x.checked),
      profitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });

    setGraphData(parseMeasurementRows(updatedArray.filter((x) => x.checked)));
    setMeasurementsToDisplay(updatedArray.filter((x) => x.checked));

    // Financial
    let financialChartMeasurements = updatedArray.sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn)).filter((x) => x.checked);
    let financialData = constructFinancialData(graphDetails, financialChartMeasurements);
    setFinancialChartData(financialData);
  };

  const onProfitLossSelectionChange = (e) => {
    var tempRows = JSON.parse(JSON.stringify(financialMeasurementRows));

    const selectedIds = e.map((index) => (tempRows[index] !== undefined ? tempRows[index].id : null));
    setProfitLossSelection(e);
    const updatedArray = tempRows.map((item) => (selectedIds.includes(item.id) ? { ...item, checked: true } : { ...item, checked: false }));

    let tempProfitLossReportData;

    formatProfitLossReportData(
      updatedArray.filter((x) => x.checked),
      profitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });
  };

  const filterDataMeasurements = (measurements, filters) => {
    let filteredMeasurements = JSON.parse(JSON.stringify(measurements)); // Clone original rows

    filteredMeasurements.forEach((x) => (x.checked = true)); // Reset checked state

    let activeFilters = filters || dataFilters; // Use provided filters or default to dataFilters

    // FILTERS
    activeFilters &&
      activeFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // Remove unchecked measurements
    filteredMeasurements = filteredMeasurements.filter((x) => x.checked);

    let dateSortedDataMeasurements = [...filteredMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    return dateSortedDataMeasurements;
  };

  const filterFinancialMeasurements = (measurements) => {
    let filteredMeasurements = measurements; // Clone original rows

    filteredMeasurements.forEach((x) => (x.checked = true)); // Reset checked state

    // FILTERS
    financialFilters &&
      financialFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // Remove unchecked measurements
    filteredMeasurements = filteredMeasurements.filter((x) => x.checked);

    let dateSortedFinancialMeasurements = [...filteredMeasurements].sort((a, b) => new Date(a.insertedOn).getTime() - new Date(b.insertedOn).getTime());
    return dateSortedFinancialMeasurements;
  };

  const onFiltersChange = (filters, dateRange, profitLossDateRanges, projection) => {
    let currentFilters;
    let currentDateRange;
    let currentProfitLossDates;

    let currentDataFilters;
    let currentFinancialFilters;

    let filteredDataMeasurements;
    let filteredFinancialMeasurements;

    let currentProjection;

    if (projection) {
      currentProjection = projection;
      setActiveProjection(JSON.parse(JSON.stringify(projection)));
    } else if (projection === "reset") {
      currentProjection = null;
      setActiveProjection(null);
    } else {
      currentProjection = activeProjection;
    }

    if (filters) {
      if (selectedGraphType === graphTypes.OUTPUT || selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.TIMELINE) {
        currentDataFilters = filters;
        setDataFilters(filters);
        currentFinancialFilters = financialFilters;
      } else if (selectedGraphType === graphTypes.FINANCIAL || selectedGraphType === graphTypes.PROFITLOSS) {
        currentFinancialFilters = filters;
        setFinancialFilters(filters);
        currentDataFilters = dataFilters;
      }
      currentFilters = filters;
      setAllFilters(filters);
    } else {
      currentDataFilters = dataFilters;
      currentFinancialFilters = financialFilters;
      currentFilters = allFilters;
    }

    if (dateRange) {
      currentDateRange = dateRange;
    } else {
      currentDateRange = timeRangeDates;
    }

    if (profitLossDateRanges) {
      currentProfitLossDates = profitLossDateRanges;
      setProfitLossDates(profitLossDateRanges);
    } else {
      currentProfitLossDates = profitLossDates;
    }

    let varFilteredResults = JSON.parse(JSON.stringify(originalMeasurementRows)).sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)); // Clone original rows

    varFilteredResults.forEach((x) => (x.checked = true)); // Reset checked state

    // Uncheck all rows that have a date outside of date indicated in dateRangeSlider
    varFilteredResults
      .filter((row) => {
        const startTime = roundToSecond(new Date(row.insertedOn)); // Convert to Date
        return startTime > currentDateRange.endDate || startTime <= currentDateRange.startDate; // Check range
      })
      .forEach((x) => (x.checked = false));

    filteredDataMeasurements = varFilteredResults.filter((x) => x.checked).map((x) => ({ ...x })); // Creates new object copies

    filteredFinancialMeasurements = varFilteredResults.filter((x) => x.checked && x.eventPrice > 0).map((x) => ({ ...x })); // Creates new object copies

    // Add projectedEventValue calculation
    if (currentProjection) {
      filteredDataMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = calculateProjection(measurement, currentProjection); // Add your projection logic here
      });

      filteredFinancialMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = calculateProjection(measurement, currentProjection); // Add your projection logic here
      });
    } else {
      filteredDataMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = null; // Reset projectedEventPrice if no projection is set
      });

      filteredFinancialMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = null; // Reset projectedEventPrice if no projection is set
      });
    }

    // FILTERS
    currentDataFilters &&
      currentDataFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredDataMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredDataMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredDataMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredDataMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredDataMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredDataMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredDataMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredDataMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredDataMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredDataMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredDataMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredDataMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredDataMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    currentFinancialFilters &&
      currentFinancialFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredFinancialMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredFinancialMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredFinancialMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredFinancialMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredFinancialMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredFinancialMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredFinancialMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredFinancialMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredFinancialMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredFinancialMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredFinancialMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredFinancialMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredFinancialMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // GENERAL

    let dateSortedDataMeasurements = [...filteredDataMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    let dateSortedFinancialMeasurements = [...filteredFinancialMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    setDataMeasurementRows(dateSortedDataMeasurements);
    setFinancialMeasurementRows(dateSortedFinancialMeasurements);

    const indexes = dateSortedDataMeasurements.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setSelection(indexes);

    const profitLossIndexes = dateSortedFinancialMeasurements.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setProfitLossSelection(profitLossIndexes);

    // YAMAZUMI
    setGraphData(parseMeasurementRows(dateSortedDataMeasurements.filter((x) => x.checked)));
    setComparePeriodMeasurements(filterDataMeasurements(originalComparePeriodMeasurements, filters));

    // Measurements to display
    setMeasurementsToDisplay(dateSortedDataMeasurements.filter((x) => x.checked));

    // PROFIT & LOSS
    let tempProfitLossReportData;
    formatProfitLossReportData(
      dateSortedFinancialMeasurements.filter((x) => x.checked),
      currentProfitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });

    // TIMELINE
    var data = chartData(dateSortedDataMeasurements.filter((x) => x.checked).flat());
    if (JSON.parse(JSON.stringify(data)) !== JSON.parse(JSON.stringify(chartRows))) {
      setChartRows(data);
    }

    // FINANCIAL
    let financialChartMeasurements = filteredFinancialMeasurements
      .sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn))
      .filter((x) => x.checked)
      .filter((x) => roundToSecond(new Date(x.insertedOn)) > new Date(currentDateRange.fromDate) && roundToSecond(new Date(x.insertedOn)) <= new Date(currentDateRange.toDate));

    let financialData = constructFinancialData(graphDetails, financialChartMeasurements);
    setFinancialChartData(financialData);

    let tempGraphProjects = [...graphProjects]; // Clone the projects array
    let fromDate = currentDateRange.fromDate;
    let toDate = currentDateRange.toDate;

    tempGraphProjects = tempGraphProjects.map((project) => {
      return {
        ...project,
        details: project.details.map((detail) => {
          return {
            ...detail,
            measurements: detail.measurements.filter((measurement) => roundToSecond(new Date(measurement.insertedOn)) > fromDate && roundToSecond(new Date(measurement.insertedOn)) <= toDate),
          };
        }),
      };
    });
    setHighLowGraphProjects(tempGraphProjects);
  };

  const calculateProjection = (measurement, projection) => {
    let projectedValue = 0;

    // Price definition projections take priority (mutually exclusive with account projections)
    if (projection?.priceDefinitions?.length > 0 && measurement.priceBundleId) {
      // Find the full bundle data from allPriceBundles
      let bundle = null;
      for (const company of allPriceBundles) {
        const found = company.priceBundles.find((b) => b.id === measurement.priceBundleId);
        if (found) {
          bundle = found;
          break;
        }
      }

      if (bundle && bundle.items.length > 0) {
        const originalTotal = bundle.items.reduce((sum, item) => sum + item.price * item.multiplier, 0);
        const projectedTotal = bundle.items.reduce((sum, item) => {
          const pdProjection = projection.priceDefinitions.find(
            (pd) => pd.priceDefinitionId === item.priceDefinitionId
          );
          const price = pdProjection ? pdProjection.newPrice : item.price;
          return sum + price * item.multiplier;
        }, 0);

        if (originalTotal > 0) {
          projectedValue = measurement.eventPrice * (projectedTotal / originalTotal);
        } else {
          projectedValue = measurement.eventPrice;
        }
      } else {
        projectedValue = measurement.eventPrice;
      }
    } else if (projection?.accounts?.some((x) => x.id === measurement.accountId)) {
      projectedValue = (measurement.eventPrice * projection?.accounts?.find((x) => x.id === measurement.accountId)?.percentage) / 100;
    } else {
      projectedValue = measurement.eventPrice;
    }
    return projectedValue;
  };

  const limitString = (str, limit) => {
    if (str.length > limit) {
      return str.substring(0, limit) + "...";
    } else {
      return str;
    }
  };

  // Detail names for financial view
  const constructDetailCollection = (details) => {
    let temporaryCollection = [];
    for (let i = 0; i < details.length; i++) {
      let thisProjectDetail = details[i];
      if (thisProjectDetail.price > 0) {
        temporaryCollection.push({ id: thisProjectDetail.id, name: thisProjectDetail.description, registrationPoint: thisProjectDetail.resourceName, color: thisProjectDetail.color });
      }
    }
    //Empty array for all details, also add a total to display in the graph
    temporaryCollection.push({ id: -1, name: "Total", color: "#c7c9c8" });

    return temporaryCollection;
  };

  // Data for financial view
  const constructFinancialData = (projectDetails, chartDataCollection) => {
    let tempTotalData = [];
    let total = 0;
    let startTime = chartDataCollection.length > 0 ? chartDataCollection[0].insertedOn : new Date();
    let count = 0;

    //First we loop through all measurements to calculate the TOTAL LINE
    chartDataCollection.forEach((thisMeasurement) => {
      let isStopwatch = false;
      let isCount = false;
      let thisDetail = projectDetails.find((x) => x.id === thisMeasurement.detailId);
      if (thisDetail !== undefined && thisDetail.price > 0) {
        if (thisDetail.controlTypeId === 2 || (thisDetail.controlTypeId === 7 && thisDetail.externalApiTypeId === 2)) {
          isStopwatch = true;
        } else if (thisDetail.controlTypeId === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId == externalApiTypes.NUMERIC) {
          isCount = true;
        }

        let price_per = thisDetail.price !== null ? thisDetail.price : 0;
        let tempMeasurementData = {};
        var dif;
        var priceToAdd;
        if (isStopwatch) {
          let startDate = new Date(thisMeasurement.startTime);
          let stopDate = new Date(thisMeasurement.stopTime);
          if (stopDate > startDate) {
            dif = (stopDate.getTime() - startDate.getTime()) / 1000;
          } else {
            dif = 0;
          }
          priceToAdd = (thisDetail.price / 3600) * dif;
        } else if (isCount) {
          priceToAdd = thisMeasurement.numericValue * price_per;
        } else {
          priceToAdd = price_per;
        }
        if (thisDetail.isProfit === thisMeasurement.isNegative) {
          total -= priceToAdd;
        } else {
          total += priceToAdd;
        }
        if (count === 0) {
          tempMeasurementData = {
            id: thisMeasurement.id,
            measurement: "Total",
            date: startTime,
            price: 0,
            profitAdded: 0,
            isVisible: true,
            "-1_Total_price": 0,
            color: thisDetail.color,
            isProfit: thisDetail.isProfit,
            isNegative: thisMeasurement.isNegative,
            originalMeasurement: thisDetail.description,
          };
          tempTotalData.push(tempMeasurementData);
        }
        tempMeasurementData = {
          id: thisMeasurement.id,
          measurement: "Total",
          date: thisMeasurement.insertedOn,
          price: priceToAdd,
          profitAdded: priceToAdd,
          isVisible: true,
          "-1_Total_price": total,
          color: thisDetail.color,
          isProfit: thisDetail.isProfit,
          isNegative: thisMeasurement.isNegative,
          originalMeasurement: thisDetail.description,
        };
        tempTotalData.push(tempMeasurementData);
        count++;
      }
    });

    let tempDetailData = [];

    //Loop through the details and their measurements to get each unique detail line
    projectDetails
      .filter((x) => x.price > 0)
      .forEach((thisDetail) => {
        let isStopwatch = false;
        let isCount = false;
        let detailCount = 0;

        if (thisDetail.controlTypeId === 2 || (thisDetail.controlTypeId === 7 && thisDetail.externalApiTypeId === 2)) {
          //Stopwatch
          isStopwatch = true;
        } else if (thisDetail.controlTypeId === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId == externalApiTypes.NUMERIC) {
          isCount = true;
        }
        let thisMeasurements = chartDataCollection.filter((x) => x.detailId === thisDetail.id);

        let total = 0;
        let price_per = thisDetail.price !== null ? thisDetail.price : 0;
        let startTime = chartDataCollection.length > 0 ? chartDataCollection[0].insertedOn : new Date();

        if (thisMeasurements.length > 0) {
          let variablename = thisDetail.id + "_" + thisDetail.description + "_price";
          thisMeasurements.forEach((thisMeasurement) => {
            let tempMeasurementData = {};
            var dif;
            var priceToAdd;
            let eventPrice = 0;
            if (isStopwatch) {
              let startDate = new Date(thisMeasurement.startTime);
              let stopDate = new Date(thisMeasurement.stopTime);
              if (stopDate > startDate) {
                dif = (stopDate.getTime() - startDate.getTime()) / 1000;
              } else {
                dif = 0;
              }
              // eventPrice = (thisDetail.price / 3600) * dif;
              priceToAdd = (thisDetail.price / 3600) * dif;
            } else if (isCount) {
              eventPrice = thisMeasurement.numericValue * price_per;
              priceToAdd = thisMeasurement.numericValue * price_per;
            } else {
              eventPrice = price_per;
              priceToAdd = price_per;
            }
            if (thisDetail.isProfit === thisMeasurement.isNegative) {
              total -= priceToAdd;
            } else {
              total += priceToAdd;
            }
            if (detailCount === 0) {
              tempMeasurementData = {
                detailCount: 0,
                id: thisMeasurement.id,
                measurement: thisDetail.description,
                date: startTime,
                price: 0,
                profitAdded: 0,
                isVisible: true,
                [variablename]: 0,
                color: thisDetail.color,
                isProfit: thisDetail.isProfit,
                isNegative: thisMeasurement.isNegative,
              };
              tempDetailData.push(tempMeasurementData);
            }
            tempMeasurementData = {
              id: thisMeasurement.id,
              measurement: thisDetail.description,
              date: thisMeasurement.insertedOn,
              price: eventPrice,
              profitAdded: priceToAdd,
              isVisible: true,
              [variablename]: total,
              color: thisDetail.color,
              isProfit: thisDetail.isProfit,
              isNegative: thisMeasurement.isNegative,
            };
            tempDetailData.push(tempMeasurementData);
            detailCount++;
          });
        }
      });
    let combinedData = tempTotalData.concat(tempDetailData);
    return combinedData;
  };

  // TIMELINE
  const chartData = (data) => {
    let tempdata = [];
    let tempObj = {};

    data.forEach((x) => {
      let startDate = new Date(x.startTime);
      let tempEndDate = new Date(x.startTime);
      let insertedOn = new Date(x.startTime);
      let updatedOn = new Date(x.startTime);

      if (x.typeId === controlTypes.STOPWATCH || (x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.STOPWATCH)) {
        if (x.updatedOn !== undefined) {
          updatedOn = x.updatedOn;
        }
        if (x.stopTime !== null && x.stopTime.length > 0) {
          tempEndDate = new Date(x.stopTime);
        }
        if (x.insertedOn !== undefined) {
          insertedOn = x.insertedOn;
        }
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Stopwatch",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.dateValue,
          key: x.keyValue,
          counter: null,
          insertedOn: insertedOn,
          updatedOn: updatedOn,
          description: limitString(x.description, 15),
          color: x.registrationPointColor,
          chartType: "rangeBar",
          argumentField: "description",
          detailId: x.detailId,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.COUNTER) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Counter",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.integerValue,
          key: x.keyValue,
          counter: x.measurementValue,
          counterDescription: limitString(x.description, 15),
          color: x.registrationPointColor,
          chartType: "scatter",
          argumentField: "counterDescription",
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.FREETEXT) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Free text",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.textValue,
          key: x.keyValue,
          counter: null,
          freetextDescription: limitString(x.description, 15),
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.DATALIST) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Data list",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          datalistDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.CAMERA) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Image",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          image: x.imageId,
          thumbnail: x.thumbnail,
          cameraDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.SCANNER) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Data list",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          datalistDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.EXTERNALAPI) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "External API",
          projectName: x.projectName,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          value: x.externalApiValues,
          externalApiDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      }

      tempdata.push(tempObj);
    });
    tempdata = tempdata.filter((x) => x.checked);
    return tempdata;
  };

  // FINANCIAL - LOGIC
  const handleDownloadImage = async (toDownload) => {
    let element;
    let fileName;
    if (toDownload === "HighLow") {
      element = highLowRef.current;
      fileName = "HighLow.jpg";
    } else {
      element = summaryRef.current;
      fileName = "Summary.jpg";
    }
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  // Data to display in financialSummaryTable
  const constructPriceData = (projects) => {
    let aggregatedPriceData = [];
    let total = 0;

    projects.forEach((project) => {
      let projectDetails = project.details;

      for (let i = 0; i < projectDetails.length; i++) {
        let thisDetail = projectDetails[i];
        let filteredMeasurements = thisDetail.measurements;

        // for each measurement, set filteredMeasurement.externalApiTypeId = thisDetail.externalApiTypeId
        filteredMeasurements.forEach((measurement) => {
          measurement.controlTypeId = thisDetail.controlTypeId;
          measurement.externalApiTypeId = thisDetail.externalApiTypeId;
        });

        if (filteredMeasurements.length > 0) {
          let detailType = filteredMeasurements[0].typeId;
          let totalTime = 0;
          let tempTotal = 0;
          let tempAmount = 0;

          if (detailType === 2 || (detailType === 7 && filteredMeasurements[0].externalApiTypeId === 2)) {
            for (let j = 0; j < filteredMeasurements.length; j++) {
              let startDate = new Date(filteredMeasurements[j].startValue);
              let stopDate = new Date(filteredMeasurements[j].stopValue);
              let dif = stopDate > startDate ? (stopDate.getTime() - startDate.getTime()) / 1000 : 0;
              totalTime += dif;
            }
            tempTotal = (thisDetail.price / 3600) * totalTime;
            tempAmount = totalTime;
          } else if (detailType === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId === externalApiTypes.NUMERIC) {
            let totalCount = filteredMeasurements.reduce((acc, x) => {
              if (x.isNegative) {
                return acc - x.numericValue;
              } else {
                return acc + x.numericValue;
              }
            }, 0);
            tempTotal = totalCount * thisDetail.price;
            tempAmount = totalCount;
          } else if (detailType === controlTypes.COUNTER) {
            let positiveMeasurements = filteredMeasurements.filter((x) => x.isNegative === false);
            let negativeMeasurements = filteredMeasurements.filter((x) => x.isNegative === true);

            let positiveTotal = positiveMeasurements.reduce((acc, x) => acc + x.price, 0);
            let negativeTotal = negativeMeasurements.reduce((acc, x) => acc + x.price, 0);

            tempTotal = positiveTotal - negativeTotal;
            tempAmount = positiveMeasurements.length - negativeMeasurements.length;
          } else {
            tempTotal = filteredMeasurements.length * (thisDetail.price !== null ? thisDetail.price : 0);
            tempAmount = filteredMeasurements.length;
          }

          let lowestDate = filteredMeasurements.reduce((minDate, measurement) => {
            let insertedOn = new Date(measurement.insertedOn);
            return insertedOn < minDate ? insertedOn : minDate;
          }, new Date(filteredMeasurements[0].insertedOn));

          let highestDate = filteredMeasurements.reduce((maxDate, measurement) => {
            let insertedOn = new Date(measurement.insertedOn);
            return insertedOn > maxDate ? insertedOn : maxDate;
          }, new Date(filteredMeasurements[0].insertedOn));

          aggregatedPriceData.push({
            projectId: project.id,
            projectName: project.projectName,
            resourceName: filteredMeasurements[0].resourceName,
            price: thisDetail.price !== null ? thisDetail.price : 0,
            totalPrice: tempTotal,
            isProfit: thisDetail.isProfit !== null ? thisDetail.isProfit : true,
            detailDescription: thisDetail.description,
            first: lowestDate,
            last: highestDate,
            amount: tempAmount,
            color: thisDetail.color,
            type: detailType,
            externalApiTypeId: filteredMeasurements[0].externalApiTypeId,
            minuteamount: !isStopwatch(filteredMeasurements[0]) ? null : convertHMS(totalTime),
          });

          if (thisDetail.isProfit) {
            total += tempTotal;
          } else {
            total -= tempTotal;
          }
        }
      }
    });

    aggregatedPriceData = aggregatedPriceData.filter((x) => x.price > 0);

    setTotalPrice(total);
    setPriceData(aggregatedPriceData);
  };

  const getControlTypeIcon = (typeId) => {
    let icon;
    switch (typeId) {
      case controlTypes.COUNTER:
        icon = <DiamondPlus className={classes.icon} />;
        break;
      case controlTypes.STOPWATCH:
        icon = <Timer className={classes.icon} />;
        break;
      case controlTypes.FREETEXT:
        icon = <TextCursorInput className={classes.icon} />;
        break;
      case controlTypes.DATALIST:
        icon = <List className={classes.icon} />;
        break;
      case controlTypes.SCANNER:
        icon = <ScanBarcode className={classes.icon} />;
        break;
      case controlTypes.CAMERA:
        icon = <Image className={classes.icon} />;
        break;
      case controlTypes.EXTERNALAPI:
        icon = <LinkIcon className={classes.icon} />;
        break;
      default:
      // code block
    }
    return icon;
  };

  const exportProfitLossReport = () => {
    if (profitLossReportComponentRef.current) {
      profitLossReportComponentRef.current.downloadReport(); // Call the function
    }
  };

  const comparePeriod = () => {
    const index = Object.keys(profitLossDates).length;

    if (index === 1) {
      setPercentageVisible(true);
    }

    if (compareLimit >= index) {
      var tempProfitLossDates = { ...profitLossDates };

      const [lastKey, lastValue] = Object.entries(tempProfitLossDates).at(0);

      var existingEndDate = lastValue.endDate;
      var existingStartDate = lastValue.startDate;

      const differenceInMs = existingEndDate - existingStartDate;
      const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);

      var newEndDate = new Date(lastValue.startDate.getTime() - 1000);
      var newStartDate = new Date(lastValue.startDate - differenceInMs);

      // for each tempProfitLossDate, set sequence to current sequence + 1
      tempProfitLossDates = Object.values(tempProfitLossDates).map((x) => {
        return { ...x, sequence: x.sequence + 1 };
      });

      tempProfitLossDates[index] = { fromDate: new Date(newStartDate), startDate: new Date(newStartDate), toDate: new Date(newEndDate), endDate: new Date(newEndDate), sequence: 1 };

      setProfitLossDates(tempProfitLossDates);

      // get earliest fromDate and startDate from profitLossDates, and latest toDate and endDate then set to timeRangeDates
      let earliestFromDate = new Date(
        Math.min.apply(
          null,
          Object.values(tempProfitLossDates).map((x) => x.fromDate)
        )
      );
      let latestToDate = new Date(
        Math.max.apply(
          null,
          Object.values(tempProfitLossDates).map((x) => x.toDate)
        )
      );

      let tempTimeRangeDates = { fromDate: earliestFromDate, toDate: latestToDate, startDate: earliestFromDate, endDate: latestToDate };
      setTimeRangeDates(tempTimeRangeDates);

      let tempProfitLossReportData;
      formatProfitLossReportData(originalMeasurementRows, tempProfitLossDates).then((formattedData) => {
        tempProfitLossReportData = formattedData;
        setProfitLossReportData(tempProfitLossReportData);
        setBoolCompareDate(true);
      });

      onFiltersChange(financialFilters, tempTimeRangeDates, tempProfitLossDates);
    } else {
      toast.error("The limit of 5 has been reached", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };

  const resetComparePeriod = () => {
    // remove all dates except the last one and change its sequence to 1
    let tempProfitLossDates = { ...profitLossDates };
    let lastKey = Object.keys(tempProfitLossDates).pop();
    let lastValue = tempProfitLossDates[lastKey];
    tempProfitLossDates = { [lastKey]: lastValue };

    // for each tempProfitLossDate, set sequence to 1
    tempProfitLossDates = Object.values(tempProfitLossDates).map((x) => {
      return { ...x, sequence: 1 };
    });

    setProfitLossDates(tempProfitLossDates);

    // get earliest fromDate and startDate from profitLossDates, and latest toDate and endDate then set to timeRangeDates
    let earliestFromDate = new Date(
      Math.min.apply(
        null,
        Object.values(tempProfitLossDates).map((x) => x.fromDate)
      )
    );
    let latestToDate = new Date(
      Math.max.apply(
        null,
        Object.values(tempProfitLossDates).map((x) => x.toDate)
      )
    );

    let tempTimeRangeDates = { fromDate: earliestFromDate, toDate: latestToDate, startDate: earliestFromDate, endDate: latestToDate };
    setTimeRangeDates(tempTimeRangeDates);

    let tempProfitLossReportData;
    formatProfitLossReportData(originalMeasurementRows, tempProfitLossDates).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
      setBoolCompareDate(false);
    });

    onFiltersChange(financialFilters, tempTimeRangeDates, tempProfitLossDates);
    setPercentageVisible(false);
  };

  const setCompareDateRange = (index, startDate, endDate, sequence) => {
    let varFilteredResults = JSON.parse(JSON.stringify(originalMeasurementRows)).sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)); // Clone original rows

    if (startDate && endDate) {
      var tempProfitLossDates = { ...profitLossDates };
      tempProfitLossDates[index] = { sequence: sequence, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) };
      let tempProfitLossReportData;
      formatProfitLossReportData(
        varFilteredResults.filter((x) => x.checked),
        tempProfitLossDates
      ).then((formattedData) => {
        tempProfitLossReportData = formattedData;
        setProfitLossReportData(tempProfitLossReportData);
        setProfitLossDates(tempProfitLossDates);
      });
    } else {
      resetCompareDateRangeFilter();
    }
  };

  const isStopwatch = (measurement) => {
    let measurementControlTypeId = measurement.controlTypeId || measurement.type;
    let measurementExternalApiTypeId = measurement.externalApiTypeId;

    if (measurementControlTypeId === controlTypes.STOPWATCH || (measurementControlTypeId === controlTypes.EXTERNALAPI && measurementExternalApiTypeId === externalApiTypes.STOPWATCH)) {
      return true;
    } else {
      return false;
    }
  };

  const isFinancialView = () => {
    return selectedGraphType === graphTypes.FINANCIAL || selectedGraphType === graphTypes.PROFITLOSS;
  };

  const isInsightsView = () => {
    return selectedGraphType === graphTypes.INSIGHTS;
  };

  const handleSetActiveProjection = (projection) => {
    if (projection === null) {
      onFiltersChange(null, timeRangeDates, profitLossDates, "reset");
    } else {
      onFiltersChange(null, timeRangeDates, profitLossDates, projection);
    }
    setActiveProjection(projection);
  };

  const resetCompareDateRangeFilter = () => {};

  return (
    <React.Fragment>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
        </div>
      )}
      {noGraphFound ? (
        <Box className={classes.noGraphContainer}>
          <p className={classes.noGraphContainer__text}>Unfortunately there is no graph available to view on this link. Make sure you have the correct URL.</p>
        </Box>
      ) : (
        <Box className="page-container">
          <Box className={classes.container}>
            <div className={classes.header__titleRow}>
              <GraphTabs
                selectedGraphType={selectedGraphType}
                handleGraphTypeSelection={handleGraphTypeSelection}
                accountCategories={allAccountCategories}
                priceBundles={allPriceBundles}
                currency={activeCurrency}
                activeProjection={activeProjection}
                handleSetActiveProjection={handleSetActiveProjection}
                {...props}
              />
            </div>
            {graphConfigurationObject !== null && (
              <div className={classes.header}>
                <div className={classes.header__titleDetailRow}>
                  <h2 className={classes.header__projectTitle}>{graphConfigurationObject.name}</h2>
                  <div className={classes.header__detailContainer}>
                    <div className={classes.header__detail}>
                      <CalendarClock className={classes.header__detailIcon} />
                      <p className={classes.header__detailText}>
                        {graphConfigurationObject.updatedOn !== null ? parseDateToDDMMYYTime(graphConfigurationObject.updatedOn) : parseDateToDDMMYYTime(graphConfigurationObject.insertedOn)}
                      </p>
                    </div>
                  </div>
                </div>
                {graphConfigurationObject.description && (
                  // <p className={classes.header__description}>{graphConfigurationObject.description}</p>
                  <TextWithReadMore dialogTitle={"Description"} text={graphConfigurationObject.description} />
                )}
              </div>
            )}
            <div className={classes.graphControlsRow}>
              <CompactDateRangePicker
                startDate={timeRangeDates.fromDate}
                endDate={timeRangeDates.endDate}
                onChange={setDateRange}
              />
              {(selectedGraphType === graphTypes.OUTPUT ||
                selectedGraphType === graphTypes.YAMAZUMI ||
                selectedGraphType === graphTypes.TIMELINE) && (
                <>
                  <TimeRangeSelector value={timeRangeZoom} onChange={handleTimeRangeZoomChange} size="medium" />
                  <TimeRangeScroll
                    startDate={viewRangeDates.fromDate}
                    endDate={viewRangeDates.endDate}
                    onPrev={handleTimeRangeScrollPrev}
                    onNext={handleTimeRangeScrollNext}
                    minDate={timeRangeDates.fromDate}
                    maxDate={timeRangeDates.toDate}
                  />
                </>
              )}
              <ProjectOrderBar projects={orderedProjectNames} onChange={setOrderedProjectNames} />
            </div>
            <div className={classes.chartContainer}>
              {(selectedGraphType === graphTypes.OUTPUT || selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.PROFITLOSS || selectedGraphType === graphTypes.INSIGHTS) && (
                <div className={classes.chartContainer__buttonContainer}>
                  <div className={classes.buttonContainer__start}>
                    {selectedGraphType === graphTypes.PROFITLOSS && (
                      <>
                        <BoolToggle
                          size="large"
                          label="Display percentages"
                          disabled={Object.keys(profitLossDates || {}).length <= 1 && !activeProjection}
                          onChange={setPercentageVisible}
                          boolValue={percentageVisible}
                        />
                        <BoolToggle
                          size="large"
                          label="Include recurring prices"
                          disabled={false}
                          onChange={setShowRecurringPrices}
                          boolValue={showRecurringPrices}
                        />
                      </>
                    )}
                    {(selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.OUTPUT) && (
                      <>
                        <BoolToggle size="large" label="Compare previous period" disabled={false} onChange={setComparePreviousPeriod} boolValue={comparePreviousPeriod} />
                        <BoolToggle disabled={false} size="large" label="Display labels" onChange={setLabelVisible} boolValue={labelVisible} />
                      </>
                    )}
                  </div>
                  <div className={classes.buttonContainer__end}>
                    {selectedGraphType === graphTypes.PROFITLOSS && (
                      <Button variant="outlined" onClick={exportProfitLossReport}>
                        Export PDF
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {selectedGraphType === graphTypes.FINANCIAL && (
                <div className={classes.timeRangePickerContainer}>
                  <div className={classes.filterBlock}>
                    {/* Functionality disabled for now */}
                    {/* {selectedGraphType === graphTypes.TIMELINE && <BoolToggle disabled={false} size="large" label="Display linear" onChange={setShowLinearTimeline} boolValue={showLinearTimeline} />} */}
                    <DateRangeSlider updateDataRange={updateDataRange} timeRangeDates={timeRangeDates}></DateRangeSlider>
                  </div>
                </div>
              )}
              <div className={classes.yamazumiContainer}>
                {selectedGraphType === graphTypes.INSIGHTS && (
                  <InsightsTab
                    allMeasurements={measurementsToDisplay}
                    graphProjects={graphProjects}
                    currency={activeCurrency}
                    loading={fetchingData}
                    timeRangeDates={timeRangeDates}
                    activeProjection={activeProjection}
                  />
                )}
                {selectedGraphType === graphTypes.OUTPUT && (
                  <>
                    {comparePreviousPeriod && !fetchingData && comparePeriodMeasurements.length === 0 && (
                      <div className={classes.compareDisclaimer}>
                        No data found for the previous period — there is nothing to compare against.
                      </div>
                    )}
                    <OutputChart
                      {...props}
                      allMeasurements={viewFilteredMeasurements}
                      compareMeasurements={comparePeriodMeasurements}
                      labelVisible={labelVisible}
                      currency={activeCurrency}
                      graphConfigurationName={graphConfigurationName}
                      loading={fetchingData}
                      emptyState={viewFilteredMeasurements.length === 0}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.YAMAZUMI && (
                  <>
                    {(selectedProjects.length === 0 || graphData.length === 0 || dataMeasurementRows.length === 0) && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>{getEmptyStateTitle()}</p>
                          <p className={classes.emptyStateCard__subtitle}>{getEmptyStateSubtitle()}</p>
                        </div>
                      </div>
                    )}
                    {comparePreviousPeriod && !fetchingData && comparePeriodMeasurements.length === 0 && (
                      <div className={classes.compareDisclaimer}>
                        No data found for the previous period — there is nothing to compare against.
                      </div>
                    )}
                    <YamazumiChart
                      {...props}
                      allMeasurements={viewFilteredMeasurements}
                      compareMeasurements={comparePeriodMeasurements}
                      currency={activeCurrency}
                      dataSource={graphData}
                      labelVisible={labelVisible}
                      graphConfigurationName={graphConfigurationName}
                      emptyState={viewFilteredMeasurements.length === 0}
                      loading={fetchingData}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.TIMELINE && (
                  <>
                    {chartRows.filter((x) => {
                      const t = new Date(x.startDate).getTime();
                      if (isNaN(t)) return false;
                      return t >= viewRangeDates.fromDate.getTime() && t <= viewRangeDates.endDate.getTime();
                    }).length === 0 && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>No measurements</p>
                          <p className={classes.emptyStateCard__subtitle}>There are no measurements to display</p>
                        </div>
                      </div>
                    )}
                    <TimeLineChart
                      data={chartRows.filter((x) => {
                        const t = new Date(x.startDate).getTime();
                        if (isNaN(t)) return false;
                        return t >= viewRangeDates.fromDate.getTime() && t <= viewRangeDates.endDate.getTime();
                      })}
                      visualisationType={showLinearTimeline ? timeLineVisualisationTypes.LINEAREVENTS : timeLineVisualisationTypes.SPLIT}
                      from={parseNewDateToDDMMYYYWithoutSeconds(viewRangeDates.fromDate)}
                      to={parseNewDateToDDMMYYYWithoutSeconds(viewRangeDates.endDate)}
                      loading={fetchingData}
                      onChartViewChange={setTimelineChartView}
                      registrationPoints={orderedProjectNames}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.FINANCIAL && (
                  <>
                    {financialChartData.length === 0 && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>No data</p>
                          <p className={classes.emptyStateCard__subtitle}>There is no financial data to display</p>
                        </div>
                      </div>
                    )}
                    <FinancialChart
                      financialChartData={financialChartData}
                      projectDetailNames={projectDetailNames}
                      currency={activeCurrency}
                      from={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.fromDate)}
                      to={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.toDate)}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.PROFITLOSS && (
                  <>
                    {(profitLossReportData === undefined || profitLossReportData.length === 0 || profitLossReportData === null) && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>No data</p>
                          <p className={classes.emptyStateCard__subtitle}>There is no financial data to display</p>
                        </div>
                      </div>
                    )}
                    <ProfitLossReport
                      ref={profitLossReportComponentRef}
                      allMeasurements={measurementsToDisplay}
                      data={financialMeasurementRows}
                      profitLossReportData={profitLossReportData}
                      from={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.fromDate)}
                      to={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.toDate)}
                      boolCompareDate={boolCompareDate}
                      comparePeriod={comparePeriod}
                      resetComparePeriod={resetComparePeriod}
                      percentageVisible={percentageVisible}
                      compareLimit={compareLimit}
                      setCompareDateRange={setCompareDateRange}
                      profitLossDates={profitLossDates}
                      currency={activeCurrency}
                      projection={activeProjection}
                      allPriceBundles={allPriceBundles}
                      loading={fetchingData}
                    />
                  </>
                )}
              </div>

              <div style={{ visibility: isFinancialView() || isInsightsView() ? "hidden" : "visible", height: isFinancialView() || isInsightsView() ? "0px" : "auto" }}>
                <DevExDataTable
                  data={selectedGraphType === graphTypes.YAMAZUMI ? filterByViewRange(yamazumiMeasurementRows) : selectedGraphType === graphTypes.OUTPUT ? filterByViewRange(outputMeasurementRows) : selectedGraphType === graphTypes.TIMELINE ? filterByViewRange(timelineChartView === "timeline" ? yamazumiMeasurementRows : timelineEventMeasurementRows) : dataMeasurementRows}
                  columns={resultColumns}
                  onSelectionChange={onSelectionChange}
                  selection={selection}
                  currentFilters={dataFilters}
                  handleFiltersChange={onFiltersChange}
                  currency={activeCurrency}
                  activeProjection={activeProjection}
                  allPriceBundles={allPriceBundles}
                  defaultHiddenColumnNames={["companyName", "projectName", "running", "action"]}
                  tableId="view-graph-results"
                />
              </div>

              <div style={{ visibility: isFinancialView() && !isInsightsView() ? "visible" : "hidden", height: isFinancialView() && !isInsightsView() ? "auto" : "0px" }}>
                <ProfitLossDevExDataTable
                  data={financialMeasurementRows}
                  columns={profitLossResultColumns}
                  currency={activeCurrency}
                  onSelectionChange={onProfitLossSelectionChange}
                  selection={profitLossSelection}
                  currentFilters={financialFilters}
                  handleFiltersChange={onFiltersChange}
                  activeProjection={activeProjection}
                  allPriceBundles={allPriceBundles}
                  defaultHiddenColumnNames={["companyName", "projectName", "running", "action"]}
                  tableId="view-graph-profitloss"
                />
              </div>
            </div>
            {selectedGraphType === graphTypes.FINANCIAL && (
              <div className={classes.financialDetailInformationContainer}>
                <div className={classes.highLowContainer} ref={highLowRef}>
                  <div className={classes.filterTitle}>
                    <span>High/Low</span>
                    <Button variant="secondary" onClick={() => handleDownloadImage("HighLow")} data-html2canvas-ignore="true">
                      Export
                    </Button>
                  </div>

                  {loading ? (
                    <Box className={classes.loadingBalls}>
                      <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
                    </Box>
                  ) : highLowGraphProjects.length > 0 ? (
                    highLowGraphProjects.map((project, projectIndex) => (
                      <Box key={`GraphProject_${projectIndex}`} className={classes.highLow__projectContainer}>
                        {/* Project subtitle */}
                        <h3 className={classes.highLow__projectSubtitle}>{project.name}</h3>

                        {/* Details of each project */}
                        {project.details.map((detail, index) => {
                          if (detail.measurements.length > 0 && detail.price > 0) {
                            return (
                              <div key={`TableHighLow_${detail.id}_${index}`} className={classes.highLow__detail}>
                                <div className={classes.highLow__headerContainer}>
                                  <div className={classes.highLow__detailHeader}>
                                    {getControlTypeIcon(detail.controlTypeId)}
                                    <p className={classes.highLow__detailTitle}>{detail.description}</p>
                                  </div>
                                  <div className={classes.headerContainer__detailInformation}>
                                    <div className={classes.detailInformation__registrationPoint}>
                                      <Tooltip title={detail.measurements[0].resourceName} placement="top">
                                        <MapPin className={classes.registrationPoint__icon} />
                                      </Tooltip>
                                      {/* <p className={classes.registrationPoint__name}>{detail.measurements[0].resourceName}</p> */}
                                    </div>
                                    {detail.isProfit ? <Plus style={{ color: "rgb(40, 167, 69)", paddingRight: "10px" }} /> : <Minus style={{ color: "rgb(220, 53, 69)", paddingRight: "10px" }} />}
                                  </div>
                                </div>
                                <Divider />
                                <TableHighLow
                                  {...props}
                                  detail={detail}
                                  projectName={project.projectName}
                                  location={project.resourceDescription}
                                  projectQr={project.qrCode}
                                  currency={activeCurrency}
                                />
                              </div>
                            );
                          } else {
                            return <React.Fragment key={`EmptyDetail_${index}`}></React.Fragment>;
                          }
                        })}
                      </Box>
                    ))
                  ) : (
                    <React.Fragment></React.Fragment>
                  )}

                  {dataMeasurementRows.length === 0 && !loading && (
                    <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center" }}>
                      <div className={classes.emptyStateCard}>
                        <p className={classes.emptyStateCard__title}>No events</p>
                        <p className={classes.emptyStateCard__subtitle}>There are no events for this project yet.</p>
                      </div>
                    </Box>
                  )}
                </div>
                <div className={classes.financialDetailInformationContainer__right}>
                  <div className={classes.pieChartsContainer}>
                    <div className={classes.pieStyle}>
                      {(!priceData || priceData.filter((x) => !isStopwatch(x)).length === 0) && (
                        <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateCard}>
                            <p className={classes.emptyStateCard__title}>No events</p>
                            <p className={classes.emptyStateCard__subtitle}>There are no event measurements during this timespan</p>
                          </div>
                        </div>
                      )}
                      <EventChart data={priceData.filter((x) => !isStopwatch(x) && x.price > 0)} title={"Event overview"} />
                    </div>
                    <div className={classes.pieStyle}>
                      {priceData.filter((x) => isStopwatch(x)).length === 0 && (
                        <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateCard}>
                            <p className={classes.emptyStateCard__title}>No stopwatches</p>
                            <p className={classes.emptyStateCard__subtitle}>There are no stopwatch measurements during this timespan</p>
                          </div>
                        </div>
                      )}
                      <EventChart data={priceData.filter((x) => isStopwatch(x))} title={"Time overview"} />
                    </div>
                  </div>
                  <div className={classes.financialSummaryContainer} ref={summaryRef}>
                    <div className={classes.calculateBoxStyle}>
                      <div className={classes.filterTitle} variant="contained" content="Options">
                        <span>Summary</span>
                        <div>
                          <Button variant="secondary" onClick={() => handleDownloadImage("Summary")} data-html2canvas-ignore="true">
                            Export
                          </Button>
                        </div>
                      </div>

                      <div>
                        <FinancialSummaryTable totalPrice={totalPrice} priceData={priceData} currency={activeCurrency} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
}

export default GraphDetailPage;

const useStyles = makeStyles((theme) => ({
  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    zIndex: 9999,
    position: "fixed",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  header__titleRow: {
    width: "100%",
    minHeight: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",

    marginBottom: "16px",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },

  // Graph type toggle
  toggleFilter: {
    height: "32px",
    boxSizing: "border-box",

    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",

    [theme.breakpoints.down("sm")]: {
      position: "relative",
      left: "0",
      transform: "none",
    },
  },

  toggleFilter__optionButton: {
    height: "100%",
    width: "128px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },

    [theme.breakpoints.down("xs")]: {
      width: "96px",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(100% / 4)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  header: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",

    backgroundColor: "var(--color-bg-card)",
    color: "var(--color-text-primary)",

    borderRadius: "12px",

    padding: "16px",
  },

  header__titleDetailRow: {
    width: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },

  header__projectTitle: {
    color: "var(--color-text-primary)",
  },

  header__description: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    margin: "0px",
  },

  header__detailContainer: {
    display: "flex",
    gap: "24px",
  },

  header__detail: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  header__detailIcon: {
    width: "20px",
    height: "20px",

    strokeWidth: "1.4px",

    display: "block",
  },

  header__detailText: {
    margin: "0px",
  },

  header__published: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  statusIndicator: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    display: "inline-block",
  },

  // Style for published indicator (green)
  published: {
    backgroundColor: "#5CC958",
  },

  // Style for not published indicator (red)
  notPublished: {
    backgroundColor: "#D84949",
  },

  noGraphContainer: {
    width: "400px",

    margin: "auto",
    marginTop: "125px",
    padding: "16px",

    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  noGraphContainer__text: {
    fontColor: "#0C0E29",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    margin: "0",
    textAlign: "center",
  },

  container: {
    boxSizing: "border-box",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    borderRadius: "8px",
    marginBottom: "24px",
    marginRight: "auto",
    marginLeft: "auto",
  },

  graphInformationContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "24px",
  },

  graphControlsRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "8px 0",
    "& > :last-child": { flex: 1, minWidth: 0 },
  },

  chartContainer: {
    width: "100%",

    position: "relative",
    zIndex: "500",

    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "16px",
    boxSizing: "border-box",
  },

  yamazumiContainer: {
    position: "relative",
    minHeight: "400px",

    marginBottom: "16px",
  },

  emptyStateContainer: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "998",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",

    textAlign: "center",

    margin: "0px",
  },

  compareDisclaimer: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 12px",
    marginBottom: "8px",
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe082",
    borderRadius: "6px",
    fontFamily: "Lato, sans-serif",
    fontSize: "13px",
    color: "#6d4c00",
  },

  chartContainer__buttonContainer: {
    width: "100%",

    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",

    marginBottom: "8px",
  },

  buttonContainer__start: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  primaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  // Filter on graph ---------------------------------------------------------------------------------------

  filterButton: {
    backgroundColor: "var(--color-primary) !important",
  },

  filterBlock: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      flexWrap: "nowrap",
      justifyContent: "flex-start",
      gap: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      justifyContent: "space-evenly",
    },
  },

  filterItem: {
    [theme.breakpoints.down("sm")]: {
      marginBottom: "16px",
      width: "100%",
      textAlign: "center",
      justifyContent: "center",
    },
  },

  clearFiltersButton: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    fontWeight: "bold",

    padding: "8px",

    border: "none",
    backgroundColor: "transparent",

    color: "rgba(12, 14, 41, 0.8)",

    "&:hover": {
      color: "rgba(12, 14, 41, 1)",
      cursor: "pointer",
    },
  },

  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",

    gap: "16px",
  },

  sharingContainer: {
    position: "relative",
    zIndex: "600",
  },

  secondaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "1px solid #a77d4f",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#a77d4f",
    backgroundColor: "transparent",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      border: "1px solid #a77d4f",
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",
    strokeWidth: "2px",

    display: "block",
  },

  switchContainer__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    gap: "8px !important",
    margin: "0px !important",

    userSelect: "none",
  },

  primaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  secondaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "1px solid var(--color-primary)",
    borderRadius: "4px",

    fontSize: "16px",
    color: "var(--color-primary)",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      border: "1px solid #a77d4f",
      cursor: "pointer",
    },
  },

  // FINANCIAL - STYLING
  financialDetailInformationContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "16px",

    marginBottom: "16px",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },

  highLowContainer: {
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    flexBasis: "0",
    flexGrow: "1",

    background: "white",
    borderRadius: "8px",

    padding: "16px",

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },

  highLow__projectContainer: {},

  highLow__projectSubtitle: {
    fontSize: "18px",
    margin: "0px",
  },

  highLow__detail: {
    display: "flex",
    flexDirection: "column",
  },

  highLow__headerContainer: {
    width: "100%",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    flexDirection: "row",
    backgroundColor: "#ede6df",
  },

  highLow__detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",

    fontSize: "16px",
    fontWeight: "600",

    padding: "10px",
  },

  highLow__detailTitle: {
    margin: "0",

    whiteSpace: "nowrap",
  },

  headerContainer__detailInformation: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  detailInformation__registrationPoint: {
    display: "flex",
    alignItems: "center",
    gap: "4px",

    // overflow ...
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    fontSize: "14px",
  },

  registrationPoint__icon: {
    height: "20px",
    width: "20px",

    strokeWidth: "1.4px",
  },

  financialDetailInformationContainer__right: {
    display: "flex",
    flexDirection: "column",
    flexBasis: "0",
    flexGrow: "2",
    gap: "16px",
  },

  pieChartsContainer: {
    width: "100%",

    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: "16px",

    overflow: "hidden",

    borderRadius: "8px",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  pieStyle: {
    width: "50%",
    boxSizing: "border-box",

    position: "relative",

    backgroundColor: "white",

    borderRadius: "8px",

    padding: "16px",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  financialSummaryContainer: {
    overflow: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px",
    gap: "10px",
  },

  filterTitle: {
    backgroundColor: "var(--color-primary) !important",
    color: "white",
    fontSize: "18px",
    fontWeight: "500",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    alignItems: "center",
  },

  icon: {
    color: "#0C0E29",
    display: "block",

    height: "24px",
    width: "24px",

    strokeWidth: "1.8px",
  },
}));